import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ActionModal from "./components/ActionModal";
import DashboardLayout from "./components/DashboardLayout";
import HrmLogin from "./components/HrmLogin";
import Toast from "./components/Toast";
import { uploadHrmInlineImage } from './api/inlineImageUpload';
import {
  addRequestComment,
  addProjectEntityComment,
  addProjectEntityChecklistItem,
  cancelExpense,
  cancelLeaveRequest,
  checkIn,
  createExpense,
  createFileUploadSlot,
  createLeaveRequest,
  createProject,
  createProjectEntity,
  createRequest,
  createReferral,
  deleteProfileDocument,
  downloadFile,
  downloadPayrollSlip,
  getAttendanceLogs,
  getAssetAllocations,
  getDefaulterLeaderboard,
  getDirectory,
  getExpenses,
  getForms,
  getHolidays,
  getLeaveBalances,
  getLeaveCredits,
  getLeaveRequests,
  getLeaveTypes,
  getManagedUsers,
  getCurrentHrmSession,
  getHrmAccessStatus,
  getPayrollSlips,
  getPolicies,
  getProjectEntity,
  getProjectEntities,
  getProfile,
  getRequest,
  getRequests,
  getTimeMonth,
  getTodayAttendance,
  logoutFromHrm,
  saveTimeWeek,
  searchProjectAccounts,
  searchProjects,
  searchTasks,
  submitTimeEntries,
  updateProjectEntity,
  updateProjectEntityChecklistItem,
  updateRequest,
  updateAddresses,
  updateEmergencyContacts,
  updateExpense,
  updateInsuranceRelated,
  updateLeaveRequest,
  updateProfile,
  updateProfilePersonalContact,
  uploadProfileDocument,
} from "./api/hrmApi";
import {
  EMPTY_EMPLOYEE_PROFILE,
  normalizeAttendance,
  normalizeAssetAllocations,
  normalizeDirectory,
  normalizeExpenses,
  normalizeHolidays,
  normalizeLeaveBalances,
  normalizeLeaveCredits,
  normalizeLeaveRequests,
  normalizeLeaveTypes,
  normalizePayrollSlips,
  normalizePolicies,
  normalizeProfile,
  normalizeProjectAccounts,
  normalizeProjectEntities,
  normalizeProjects,
  normalizeRequestComments,
  normalizeRequests,
  normalizeTasks,
  normalizeTimesheetMonth,
  normalizeUser,
  normalizeUsers,
} from "./api/hrmNormalizers";
import {
  onboardingTasks as initialOnboardingTasks,
} from "./data/hrmMockData";
import { buildProjectEntityStatusUpdatePayload } from "./utils/projectEntityPayloads";
import "./styles/newHrm.css";
import "./styles/premiumHrm.css";
import "./styles/revampedHrm.css";
import "./styles/dashboardWorkspace.css";

const SESSION_KEY = "newHrmSession";

const mergeLeaveRequests = (requests = [], preservedRequests = []) => {
  const merged = [];
  const seenIds = new Set();

  [...requests, ...preservedRequests].forEach((request) => {
    const requestId = String(request?.id || "").trim();
    if (requestId && seenIds.has(requestId)) {
      return;
    }
    if (requestId) {
      seenIds.add(requestId);
    }
    merged.push(request);
  });

  return merged;
};
const AWC_USER_KEY = "awcUser";
const AWC_ACCESS_TOKEN_KEY = "awcAccessToken";
const AWC_TOKEN_EXPIRES_AT_KEY = "awcTokenExpiresAt";
const ADMIN_EMAILS = new Set(["mayank@antrazal.com", "shiv@antrazal.com"]);
const PROJECT_CREATOR_EMAILS = new Set(["mayank@antrazal.com", "shiv@antrazal.com"]);
const RETREAT_EVENT_ID = "retreat-2026";
const CASE_WORKER_EMAILS = new Set([
  "mayank@antrazal.com",
  "shiv@antrazal.com",
  "anjali.kalwani@antrazal.com",
  "anjali@antrazal.com",
  "ashish.jangid@antrazal.com",
  "ashish@antrazal.com",
]);
const REQUIRED_DAILY_TIMESHEET_HOURS = 8;
const SCREEN_RESOURCES = {
  dashboard: ["attendance", "balances", "leaveRequests", "holidays", "requests", "entities", "profile"],
  time: ["allocatedProjects", "entities"],
  leave: ["attendance", "balances", "leaveCredits", "leaveRequests", "leaveTypes", "holidays"],
  payroll: ["payroll", "profile"],
  profile: ["profile"],
  myForms: ["forms"],
  policies: ["policies"],
  requests: ["requests"],
  myCases: ["workerRequests"],
  expenses: ["expenses"],
  directory: ["directory"],
  projects: ["projects", "entities", "directory"],
  assets: ["assets"],
  benefits: ["profile"],
};
const HRM_NAV_ITEMS = new Set([
  "dashboard",
  "profile",
  "myForms",
  "events",
  "projects",
  "time",
  "leave",
  "payroll",
  "policies",
  "expenses",
  "assets",
  "benefits",
  "directory",
  "performance",
  "learning",
  "requests",
  "myCases",
]);

const getInitialPortalRoute = () => {
  if (typeof window === "undefined") {
    return { nav: "dashboard", entityId: "", eventId: "", requestId: "", prefersAssignedCases: false };
  }
  const params = new URLSearchParams(window.location.search || "");
  const entityId = params.get("entityId") || params.get("projectEntityId") || params.get("ticketId") || "";
  const projectId = params.get("projectId") || "";
  const requestId = params.get("requestId") || params.get("caseId") || "";
  const requestedEventId = params.get("event") || params.get("eventId") || "";
  const eventId = requestedEventId === RETREAT_EVENT_ID ? requestedEventId : "";
  const requestedNav = (params.get("tab") || params.get("nav") || params.get("screen") || "").trim().toLowerCase();
  const prefersAssignedCases = ["case", "cases", "mycases", "my-cases", "my_cases"].includes(requestedNav);
  const prefersForms = ["form", "forms", "myforms", "my-forms", "my_forms"].includes(requestedNav);
  const resolvedNav =
    requestedNav === "case" || requestedNav === "cases" || requestedNav === "request"
      ? "requests"
      : prefersForms
        ? "myForms"
      : requestedNav === "mycases" || requestedNav === "my-cases" || requestedNav === "my_cases"
        ? "myCases"
        : requestedNav;
  return {
    nav: entityId || projectId
      ? "projects"
      : requestId
        ? "requests"
        : eventId
          ? "events"
          : HRM_NAV_ITEMS.has(resolvedNav)
            ? resolvedNav
            : "dashboard",
    entityId,
    eventId,
    requestId,
    prefersAssignedCases,
  };
};

const formatMinutes = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${String(remainingMinutes).padStart(2, "0")}m`;
};

const formatHoursValue = (value) => {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  if (typeof value === "string" && value.toLowerCase().includes("h")) {
    return value;
  }
  const numericHours = Number(value);
  return Number.isFinite(numericHours) ? formatMinutes(Math.round(numericHours * 60)) : String(value);
};

const getEntryItems = (entry) => {
  if (entry?.items?.length) {
    return entry.items;
  }
  return entry ? [entry] : [];
};

const parseEntryHoursToMinutes = (entry) =>
  Math.round(getEntryItems(entry).reduce((sum, item) => sum + Number(item.hours || 0), 0) * 60);

const getEntryHours = (entry) => parseEntryHoursToMinutes(entry) / 60;

const normalizeComparableText = (value) => String(value || "").trim().toLowerCase();

const unwrapDefaulterLeaderboardPayload = (payload) => {
  let current = payload;
  for (let depth = 0; depth < 4; depth += 1) {
    if (!current || typeof current !== "object") {
      return {};
    }
    if (Array.isArray(current.defaulters) || current.title || current.summary) {
      return current;
    }
    if (current.data && typeof current.data === "object") {
      current = current.data;
      continue;
    }
    return current;
  }
  return current && typeof current === "object" ? current : {};
};

const normalizeDefaulterLeaderboard = (payload) => {
  const data = unwrapDefaulterLeaderboardPayload(payload);
  const defaulters = Array.isArray(data.defaulters) ? data.defaulters : [];
  return {
    title: data.title || "HRM Defaulter",
    generatedAt: data.generatedAt || "",
    summary: data.summary || {},
    defaulters,
  };
};

const getAttendanceDate = (date = new Date()) =>
  `Today, ${date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })}`;

const getAttendanceLogDate = (date = new Date()) =>
  date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const getAttendanceDateKey = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "Asia/Kolkata",
    year: "numeric",
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  return `${year}-${month}-${day}`;
};

const getAttendanceRecordDateKey = (attendanceRecord, fallbackDate = new Date()) => {
  const rawValue = String(attendanceRecord?.rawLoggedDate || attendanceRecord?.attendanceDate || "").trim();
  const isoMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
  return isoMatch ? isoMatch[1] : getAttendanceDateKey(fallbackDate);
};

const getAttendanceClockTime = (date = new Date()) =>
  date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });

const isWeekendDateValue = (dateValue) => {
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  return date.getDay() === 0 || date.getDay() === 6;
};

const getInitialTimesheetDate = (monthId) => {
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  if (todayKey.startsWith(`${monthId}-`)) {
    return todayKey;
  }

  const [year, month] = String(monthId || "").split("-").map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  for (let day = 1; day <= daysInMonth; day += 1) {
    const dateValue = `${monthId}-${String(day).padStart(2, "0")}`;
    if (!isWeekendDateValue(dateValue)) {
      return dateValue;
    }
  }

  return `${monthId}-01`;
};

const TIMESHEET_DAY_IDS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const getMonthId = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const getMonthLabel = (monthId) => {
  const [year, month] = monthId.split("-").map(Number);
  const date = new Date(year, month - 1, 1);
  if (Number.isNaN(date.getTime())) {
    return monthId;
  }
  return date.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

const getTodayDateKey = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
};

const isPreviousTimesheetMonthId = (monthId) =>
  /^\d{4}-(0[1-9]|1[0-2])$/.test(String(monthId || "")) && monthId < getMonthId(new Date());

const isApprovedTimesheetMonth = (month) => String(month?.status || "").toLowerCase() === "approved";

const getTimesheetMonthLockMessage = (month) => {
  if (isPreviousTimesheetMonthId(month?.id)) {
    return `${month?.label || "Selected month"} is closed. Previous month timesheets cannot be edited or submitted.`;
  }
  if (isApprovedTimesheetMonth(month)) {
    return `${month?.label || "Selected month"} timesheet is approved and cannot be edited.`;
  }
  return "";
};

const createBlankTimesheetMonth = (monthId) => {
  const [year, month] = monthId.split("-").map(Number);
  const monthName = new Date(year, month - 1, 1).toLocaleDateString("en-IN", { month: "short" });
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayMondayIndex = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const monthNumber = String(month).padStart(2, "0");
  const weeks = [];

  for (let weekStartDay = 1 - firstDayMondayIndex, index = 1; weekStartDay <= daysInMonth; weekStartDay += 7, index += 1) {
    const startDay = Math.max(1, weekStartDay);
    const endDay = Math.min(weekStartDay + 6, daysInMonth);
    const entries = {};

    TIMESHEET_DAY_IDS.forEach((dayId, dayIndex) => {
      const dayOfMonth = weekStartDay + dayIndex;
      const isOutsideMonth = dayOfMonth < 1 || dayOfMonth > daysInMonth;
      entries[dayId] = {
        workDate: isOutsideMonth ? "" : `${year}-${monthNumber}-${String(dayOfMonth).padStart(2, "0")}`,
        status: "Draft",
        items: [],
        isOutsideMonth,
      };
    });

    weeks.push({
      id: `week_${year}_${monthNumber}_${index}`,
      label: `Week ${index}`,
      range: `${String(startDay).padStart(2, "0")}-${String(endDay).padStart(2, "0")} ${monthName}`,
      status: "Draft",
      entries,
    });
  }

  return {
    id: monthId,
    label: getMonthLabel(monthId),
    status: "Draft",
    totalHours: "",
    weeks,
  };
};

const createInitialTimesheetMonths = () => {
  const today = new Date();
  return Array.from({ length: 24 }, (_, index) => {
    const date = new Date(today.getFullYear(), today.getMonth() - index, 1);
    return createBlankTimesheetMonth(getMonthId(date));
  });
};

const initialTimesheetMonths = createInitialTimesheetMonths();

const getWeekForTimesheetDate = (month, dateValue) => {
  const selectedDay = Number(dateValue?.slice(-2));
  if (!month || Number.isNaN(selectedDay)) {
    return month?.weeks?.[0];
  }

  return (
    month.weeks.find((week) => {
      const rangeMatch = String(week.range || "").match(/^(\d{2})-(\d{2})/);
      if (!rangeMatch) {
        return false;
      }
      const startDay = Number(rangeMatch[1]);
      const endDay = Number(rangeMatch[2]);
      return selectedDay >= startDay && selectedDay <= endDay;
    }) || month.weeks[0]
  );
};

const getDayIdForDate = (dateValue) => {
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][date.getDay()];
};

const getTimesheetEntryStatus = (entry, month) => entry?.status || (month?.status === "Approved" ? "Approved" : "Draft");

const getTimesheetCellId = (monthId, weekId, dayId) => `${monthId}:${weekId}:${dayId}`;

const getWorkDateForWeekDay = (monthId, week, dayKey) => {
  const [year, month] = String(monthId || "").split("-").map(Number);
  const dayIndexByKey = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
  const targetDayIndex = dayIndexByKey[dayKey];
  const rangeMatch = String(week?.range || "").match(/^(\d{1,2})-(\d{1,2})/);
  const startDay = Number(rangeMatch?.[1]);
  const endDay = Number(rangeMatch?.[2]);

  if (!year || !month || targetDayIndex === undefined || !startDay || !endDay) {
    return "";
  }

  const daysInMonth = new Date(year, month, 0).getDate();
  for (let dayOfMonth = startDay; dayOfMonth <= Math.min(endDay, daysInMonth); dayOfMonth += 1) {
    const date = new Date(year, month - 1, dayOfMonth);
    if (date.getDay() === targetDayIndex) {
      return `${year}-${String(month).padStart(2, "0")}-${String(dayOfMonth).padStart(2, "0")}`;
    }
  }

  return "";
};

const hasTimeItemInput = (item = {}) =>
  Boolean(
    String(item.hours || "").trim() ||
      String(item.project || item.projectName || item.projectId || "").trim() ||
      String(item.task || item.taskName || item.taskTitle || item.taskId || "").trim() ||
      String(item.description || "").trim()
  );

const isRejectedTimeStatus = (status) =>
  String(status || "").trim().toLowerCase().replace(/[\s_-]+/g, "") === "rejected";

const getTimesheetMutationStatus = (status) => (isRejectedTimeStatus(status) ? "Draft" : status || "Draft");

const normalizeTimeStatusKey = (status) =>
  String(status || "").trim().toLowerCase().replace(/[\s_-]+/g, "");

const isEditableTimeStatus = (status) => {
  const normalizedStatus = normalizeTimeStatusKey(status);
  return !normalizedStatus || normalizedStatus === "draft" || normalizedStatus === "rejected";
};

const isLockedTimeStatus = (status) => !isEditableTimeStatus(status);

const isLeaveTimeItem = (item = {}) => {
  if (
    item.isLeaveEntry ||
    item.isLeaveTimeEntry ||
    item.leaveEntry ||
    item.leaveTimeEntry ||
    item.isLeave
  ) {
    return true;
  }

  const descriptor = String(`${item.description || ""} ${item.source || ""} ${item.entryType || ""}`)
    .trim()
    .toLowerCase();
  return (
    descriptor.includes("auto-created from approved leave") ||
    descriptor.includes("took leave") ||
    descriptor.includes("leave time entry")
  );
};

const isReadOnlyTimeItem = (item = {}, status = "") =>
  Boolean(isLeaveTimeItem(item) || item.lockRows || item.readOnly || item.canEdit === false || isLockedTimeStatus(status));

const getTimeItemRowKey = (item = {}) => item.rowKey || item.clientRowId || item.id;

const getTimeEntryRecordId = (item = {}) =>
  getCrmRecordId(item.timeEntryId) ||
  getCrmRecordId(item.timeSheetEntryId) ||
  getCrmRecordId(item.timesheetEntryId) ||
  getCrmRecordId(item.timeCardEntryId) ||
  getCrmRecordId(item.timecardEntryId) ||
  getCrmRecordId(item.entryId) ||
  getCrmRecordId(item.recordId) ||
  getCrmRecordId(item.salesforceId) ||
  getCrmRecordId(item.sfId) ||
  getCrmRecordId(item.Time_Entry__c) ||
  getCrmRecordId(item.Time_Sheet_Entry__c) ||
  getCrmRecordId(item.Time_Card_Entry__c) ||
  getCrmRecordId(item.Timesheet_Entry__c) ||
  getCrmRecordId(item.Timecard_Entry__c) ||
  getCrmRecordId(item.TSE__c) ||
  getCrmRecordId(item.timeItemId) ||
  getCrmRecordId(item.id);

const getDeletedNonApprovedTimeItems = (currentEntry = {}, originalEntry = {}, week = {}, fallbackWorkDate = "") => {
  const keptItemKeys = new Set(
    getEntryItems(currentEntry)
      .map(getTimeItemRowKey)
      .filter(Boolean)
  );
  const originalStatus = originalEntry?.status || week?.status || "Draft";

  return getEntryItems(originalEntry)
    .map((item) => {
      const id = getTimeEntryRecordId(item);
      return {
        id,
        timeEntryId: id,
        rowKey: getTimeItemRowKey(item),
        workDate: item.workDate || originalEntry?.workDate || fallbackWorkDate,
        status: item.status || originalStatus,
        description: item.description,
        lockRows: item.lockRows,
        readOnly: item.readOnly,
        canEdit: item.canEdit,
        isLeaveEntry: item.isLeaveEntry || item.isLeaveTimeEntry || item.leaveEntry || item.leaveTimeEntry || item.isLeave,
      };
    })
    .filter((item) => item.id && !keptItemKeys.has(item.rowKey || item.id) && !isReadOnlyTimeItem(item, item.status));
};

const getExplicitDeletedNonApprovedTimeItems = (currentEntry = {}, week = {}, fallbackWorkDate = "") => {
  const deletedItems = [
    ...(Array.isArray(currentEntry.deletedEntries) ? currentEntry.deletedEntries : []),
    ...(Array.isArray(currentEntry.deletedItems) ? currentEntry.deletedItems : []),
    ...(Array.isArray(currentEntry.deletedTimeEntries) ? currentEntry.deletedTimeEntries : []),
  ];
  const fallbackStatus = currentEntry?.status || week?.status || "Draft";

  return deletedItems
    .map((item) => {
      const id = getTimeEntryRecordId(item);
      return {
        ...item,
        id,
        timeEntryId: id,
        workDate: item.workDate || currentEntry?.workDate || fallbackWorkDate,
        status: item.status || fallbackStatus,
      };
    })
    .filter((item) => item.id && !isReadOnlyTimeItem(item, item.status));
};

const uniqueTimeDeleteItems = (items = []) => {
  const seenIds = new Set();
  return items.filter((item) => {
    if (!item?.id || seenIds.has(item.id)) {
      return false;
    }
    seenIds.add(item.id);
    return true;
  });
};

const getCrmRecordId = (value) => {
  const id = String(value || "").trim();
  return /^[a-zA-Z0-9]{15,18}$/.test(id) ? id : "";
};

const trimStringValue = (value) => String(value || "").trim();

const previewTextFromRichComment = (value = "") => {
  const rawValue = trimStringValue(value);
  if (!rawValue) {
    return "";
  }
  const valueWithBreaks = rawValue
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, "\n");
  const text =
    typeof window !== "undefined" && window.DOMParser
      ? new window.DOMParser().parseFromString(valueWithBreaks, "text/html").body.textContent || ""
      : valueWithBreaks.replace(/<[^>]+>/g, " ");
  return text.replace(/\s+/g, " ").trim();
};

const projectEntityMatchesId = (entity = {}, entityId = "") => {
  const requestedId = trimStringValue(entityId);
  if (!requestedId) {
    return false;
  }

  return [entity.id, entity.key, entity.entityId, entity.taskId, entity.recordId, entity.salesforceId]
    .filter(Boolean)
    .map(String)
    .includes(requestedId);
};

const getProjectFileKey = (file = {}) =>
  getCrmRecordId(file.fileId || file.contentDocumentId || file.id || file.versionId) ||
  trimStringValue(file.downloadUrl || file.publicUrl || file.name || file.fileName || file.title);

const mergeProjectFiles = (primaryFiles = [], fallbackFiles = []) => {
  const filesByKey = new Map();
  [...primaryFiles, ...fallbackFiles].forEach((file) => {
    const key = getProjectFileKey(file);
    if (key && !filesByKey.has(key)) {
      filesByKey.set(key, file);
    }
  });
  return Array.from(filesByKey.values());
};

const mergeProjectEntityRow = (incoming = {}, existing = {}) => {
  const incomingAttachments = Array.isArray(incoming.attachments) ? incoming.attachments : [];
  const existingAttachments = Array.isArray(existing.attachments) ? existing.attachments : [];
  const incomingComments = Array.isArray(incoming.comments) ? incoming.comments : [];
  const existingComments = Array.isArray(existing.comments) ? existing.comments : [];
  const attachments = mergeProjectFiles(incomingAttachments, existingAttachments);
  return {
    ...existing,
    ...incoming,
    attachments,
    files: attachments,
    attachmentCount: Math.max(Number(incoming.attachmentCount || 0), Number(existing.attachmentCount || 0), attachments.length),
    comments: incomingComments.length ? incomingComments : existingComments,
    commentCount: Math.max(Number(incoming.commentCount || 0), Number(existing.commentCount || 0), incomingComments.length || existingComments.length),
  };
};

const mergeProjectEntityRows = (currentRows = [], incomingRows = []) => {
  const rowsById = new Map();
  currentRows.forEach((row) => {
    const id = trimStringValue(row?.id);
    if (id) {
      rowsById.set(id, row);
    }
  });
  incomingRows.forEach((row) => {
    const id = trimStringValue(row?.id);
    if (id) {
      rowsById.set(id, mergeProjectEntityRow(row, rowsById.get(id)));
    }
  });
  return Array.from(rowsById.values());
};

const getProjectOptionId = (project = {}) => getCrmRecordId(project.id || project.projectId || project.Project__c);

const getTaskProjectId = (task = {}) => getCrmRecordId(task.projectId || task.Project__c || task.project?.id || task.Project__r?.Id);

const getTaskProjectName = (task = {}) =>
  normalizeComparableText(task.projectName || task.projectTitle || task.project || task.Project__r?.Name || task.Project__r?.Title__c);

const findProjectOption = (projectOptions = [], { projectId = "", project = "" } = {}) => {
  const cleanProjectId = getCrmRecordId(projectId);
  const cleanProjectName = normalizeComparableText(project);
  return projectOptions.find((projectOption) =>
    (cleanProjectId && getProjectOptionId(projectOption) === cleanProjectId) ||
    (cleanProjectName && normalizeComparableText(projectOption.name) === cleanProjectName)
  );
};

const isTaskForProjectOption = (task = {}, project = {}) => {
  const projectId = getProjectOptionId(project);
  const taskProjectId = getTaskProjectId(task);
  if (projectId && taskProjectId) {
    return projectId === taskProjectId;
  }

  const projectName = normalizeComparableText(project.name || project.projectName);
  const taskProjectName = getTaskProjectName(task);
  return Boolean(projectName && taskProjectName && projectName === taskProjectName);
};

const findTaskOptionForProject = (taskOptions = [], taskName = "", project = {}) => {
  const cleanTaskName = normalizeComparableText(taskName);
  if (!cleanTaskName || !project) {
    return undefined;
  }
  return taskOptions.find((task) => normalizeComparableText(task.name) === cleanTaskName && isTaskForProjectOption(task, project));
};

const hasProjectAllocation = (project = {}) =>
  Boolean(
    project.isAllocated ||
      project.allocated ||
      project.hasAllocation ||
      project.allocationId ||
      project.projectAllocationId ||
      project.Project_Allocation__c ||
      project.ANT_Project_Allocation__c
  );

const getAllocatedTimeProjectOptions = (projectOptions = [], taskOptions = []) => {
  const taskProjectIds = new Set(taskOptions.map(getTaskProjectId).filter(Boolean));
  const taskProjectNames = new Set(taskOptions.map(getTaskProjectName).filter(Boolean));
  const projectsByKey = new Map();

  projectOptions.forEach((project) => {
    const projectId = getProjectOptionId(project);
    const projectName = normalizeComparableText(project.name || project.projectName || project.title);
    const canUseForTime =
      hasProjectAllocation(project) ||
      (projectId && taskProjectIds.has(projectId)) ||
      (projectName && taskProjectNames.has(projectName));

    if (!canUseForTime) {
      return;
    }

    projectsByKey.set(projectId || projectName || String(projectsByKey.size), project);
  });

  return Array.from(projectsByKey.values());
};

const trimObjectStringValues = (source = {}) =>
  Object.entries(source || {}).reduce((payload, [key, value]) => ({
    ...payload,
    [key]: typeof value === "string" ? value.trim() : value,
  }), {});

const isIsoDateValue = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim());

const toIsoDateKey = (value) => {
  const cleanValue = trimStringValue(value);
  if (!cleanValue) {
    return "";
  }

  const isoMatch = cleanValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) {
    return isoMatch[1];
  }

  const date = new Date(cleanValue.includes("T") ? cleanValue : `${cleanValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const getNextDateKey = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  date.setDate(date.getDate() + 1);
  return toIsoDateKey(date);
};

const getWeekendDayName = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleDateString("en-GB", { weekday: "long" });
};

const getLeaveWeekendConflicts = (startDate, endDate) => {
  const startKey = toIsoDateKey(startDate);
  const endKey = toIsoDateKey(endDate);
  if (!startKey || !endKey || endKey < startKey) {
    return [];
  }

  const conflicts = [];
  for (let dateKey = startKey; dateKey && dateKey <= endKey; dateKey = getNextDateKey(dateKey)) {
    const date = new Date(`${dateKey}T00:00:00`);
    const day = date.getDay();
    if (day === 0 || day === 6) {
      conflicts.push({
        date: dateKey,
        name: getWeekendDayName(dateKey),
      });
    }
  }

  return conflicts;
};

const formatLeaveWeekendToast = (conflicts = []) => {
  const weekendLabels = conflicts.slice(0, 3).map((day) => `${day.name} (${day.date})`);
  const remainingCount = conflicts.length - weekendLabels.length;
  const suffix = remainingCount > 0 ? ` and ${remainingCount} more` : "";
  return `Leave cannot be applied on weekends: ${weekendLabels.join(", ")}${suffix}. Split the request around weekends.`;
};

const getLeaveHolidayConflicts = (startDate, endDate, holidays = []) => {
  const startKey = toIsoDateKey(startDate);
  const endKey = toIsoDateKey(endDate);
  if (!startKey || !endKey || endKey < startKey || !holidays.length) {
    return [];
  }

  const holidaysByDate = holidays.reduce((map, holiday) => {
    const dateKey = toIsoDateKey(holiday?.dateKey || holiday?.rawDate || holiday?.holidayDate || holiday?.Holiday_Date__c || holiday?.date);
    if (dateKey && !map.has(dateKey)) {
      map.set(dateKey, holiday);
    }
    return map;
  }, new Map());
  const conflicts = [];

  for (let dateKey = startKey; dateKey && dateKey <= endKey; dateKey = getNextDateKey(dateKey)) {
    const holiday = holidaysByDate.get(dateKey);
    if (holiday) {
      conflicts.push({
        date: holiday.date || dateKey,
        name: holiday.name || holiday.title || holiday.Name || "Holiday",
      });
    }
  }

  return conflicts;
};

const formatLeaveHolidayToast = (conflicts = []) => {
  const holidayLabels = conflicts.slice(0, 3).map((holiday) => `${holiday.name} (${holiday.date})`);
  const remainingCount = conflicts.length - holidayLabels.length;
  const suffix = remainingCount > 0 ? ` and ${remainingCount} more` : "";
  return `Leave cannot be applied on company holidays: ${holidayLabels.join(", ")}${suffix}.`;
};

const isBlockingLeaveRequestStatus = (status) => {
  const normalizedStatus = String(status || "").trim().toLowerCase();
  return normalizedStatus && !normalizedStatus.includes("reject") && !normalizedStatus.includes("cancel");
};

const isHalfDayLeaveRequest = (request = {}) => {
  const durationText = String(request?.duration || request?.durationType || request?.Duration_Type__c || "").toLowerCase();
  return Number(numericLeaveDays(request)) === 0.5 || durationText.includes("half");
};

const getLeaveOverlapConflicts = (startDate, endDate, leaveRequests = [], requestedLeaveDays = 1) => {
  const startKey = toIsoDateKey(startDate);
  const endKey = toIsoDateKey(endDate);
  if (!startKey || !endKey || endKey < startKey) {
    return [];
  }

  const requestedDays = Number(requestedLeaveDays || 0);
  const canShareHalfDayDate = startKey === endKey && requestedDays === 0.5;
  const existingSameDayHalfDays = (leaveRequests || []).reduce((sum, request) => {
    if (!isBlockingLeaveRequestStatus(request?.status)) {
      return sum;
    }
    const requestStart = toIsoDateKey(request?.startDateKey || request?.startDate || request?.date);
    const requestEnd = toIsoDateKey(request?.endDateKey || request?.endDate || request?.date || requestStart) || requestStart;
    if (requestStart === startKey && requestEnd === endKey && isHalfDayLeaveRequest(request)) {
      return sum + 0.5;
    }
    return sum;
  }, 0);

  return (leaveRequests || []).filter((request) => {
    if (!isBlockingLeaveRequestStatus(request?.status)) {
      return false;
    }
    const requestStart = toIsoDateKey(request?.startDateKey || request?.startDate || request?.date);
    const requestEnd = toIsoDateKey(request?.endDateKey || request?.endDate || request?.date || requestStart) || requestStart;
    const overlaps = requestStart && requestEnd && requestStart <= endKey && requestEnd >= startKey;
    if (!overlaps) {
      return false;
    }
    if (
      canShareHalfDayDate &&
      requestStart === startKey &&
      requestEnd === endKey &&
      isHalfDayLeaveRequest(request) &&
      existingSameDayHalfDays + requestedDays <= 1
    ) {
      return false;
    }
    return true;
  });
};

const formatLeaveOverlapToast = (conflicts = []) => {
  if (!conflicts.length) {
    return "";
  }

  const request = conflicts[0];
  const range = request.endDate && request.endDate !== request.date ? `${request.date} - ${request.endDate}` : request.date;
  return `Leave already exists for ${range} (${request.status}). Cancel or reject the existing request before applying again.`;
};

const normalizeLeaveTypeKey = (value) => String(value || "").trim().toLowerCase();

const isPendingLeaveReservationStatus = (status) => {
  const normalizedStatus = String(status || "").trim().toLowerCase();
  return normalizedStatus.includes("pending") || normalizedStatus.includes("submitted") || normalizedStatus.includes("review");
};

const numericLeaveDays = (request) => {
  const directValue = Number(request?.leaveDays ?? request?.days ?? request?.durationDays);
  if (Number.isFinite(directValue)) {
    return directValue;
  }

  const parsedValue = parseFloat(String(request?.duration || ""));
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getPendingLeaveReservationsByType = (leaveRequests = []) =>
  (leaveRequests || []).reduce((reservations, request) => {
    if (!isPendingLeaveReservationStatus(request?.status)) {
      return reservations;
    }

    const leaveTypeKey = normalizeLeaveTypeKey(request?.leaveType || request?.type);
    const days = numericLeaveDays(request);
    if (!leaveTypeKey || days <= 0) {
      return reservations;
    }

    return {
      ...reservations,
      [leaveTypeKey]: Number(((reservations[leaveTypeKey] || 0) + days).toFixed(2)),
    };
  }, {});

const applyPendingLeaveReservations = (leaveBalances = [], leaveRequests = []) => {
  const reservationsByType = getPendingLeaveReservationsByType(leaveRequests);
  return (leaveBalances || []).map((balance) => {
    const leaveTypeKey = normalizeLeaveTypeKey(balance?.label || balance?.leaveType || balance?.type);
    const rawRemaining = Number(balance?.remaining ?? balance?.balance ?? 0);
    const safeRawRemaining = Number.isFinite(rawRemaining) ? rawRemaining : 0;
    const pendingReserved = Number(reservationsByType[leaveTypeKey] || 0);
    const adjustedRemaining = Math.max(safeRawRemaining - pendingReserved, 0);

    return {
      ...balance,
      rawRemaining: safeRawRemaining,
      pendingReserved,
      remaining: Number(adjustedRemaining.toFixed(2)),
    };
  });
};

const normalizeAddressType = (value) => {
  const text = trimStringValue(value).toLowerCase();
  return text === "other" || text.includes("current") ? "Current Living Address" : "Mailing Address";
};

const cleanOptionalDate = (value) => {
  const dateValue = trimStringValue(value);
  return isIsoDateValue(dateValue) ? dateValue : "";
};

const normalizeProfileBoolean = (value) => {
  if (typeof value === "boolean") {
    return value;
  }
  return ["true", "yes", "1", "y"].includes(trimStringValue(value).toLowerCase());
};

const cleanEstimatedHours = (value) => {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  const numericHours = Number(value);
  return Number.isFinite(numericHours) && numericHours >= 0 ? numericHours : null;
};

const withRealId = (row = {}) => {
  const id = getCrmRecordId(row.id || row.Id);
  return id ? { id } : {};
};

const hasProfileRowValue = (row = {}, keys = []) =>
  Boolean(getCrmRecordId(row.id || row.Id) || keys.some((key) => trimStringValue(row[key])));

const normalizeProfileSavePayload = (section, payload = {}) => {
  if (section === "personal" || section === "contact") {
    const personal = trimObjectStringValues(payload.personal || {});
    const contact = trimObjectStringValues(payload.contact || {});
    return {
      ...trimObjectStringValues(payload),
      ...personal,
      ...contact,
      personal,
      contact,
    };
  }

  if (section === "emergency") {
    const emergencyContacts = (payload.emergencyContacts || [])
      .map((contact) => {
        const normalizedContact = {
          ...withRealId(contact),
          name: trimStringValue(contact.name),
          relationship: trimStringValue(contact.relationship) || "Other",
          phone: trimStringValue(contact.phone),
          email: trimStringValue(contact.email),
        };
        return {
          ...normalizedContact,
          contactName: normalizedContact.name,
          Contact_Name__c: normalizedContact.name,
          Relationship__c: normalizedContact.relationship,
          Phone__c: normalizedContact.phone,
          Email__c: normalizedContact.email,
        };
      })
      .filter((contact) => hasProfileRowValue(contact, ["name", "phone", "email"]));
    return {
      emergencyContacts,
      emergencyContactList: emergencyContacts,
      contacts: emergencyContacts,
    };
  }

  if (section === "addresses") {
    const addresses = (payload.addresses || [])
      .map((address) => {
        const normalizedAddress = {
          ...withRealId(address),
          type: normalizeAddressType(address.type),
          line1: trimStringValue(address.line1 || address.street),
          city: trimStringValue(address.city),
          state: trimStringValue(address.state),
          country: trimStringValue(address.country),
          zipCode: trimStringValue(address.zipCode),
        };
        return {
          ...normalizedAddress,
          addressType: normalizedAddress.type,
          Address_Type__c: normalizedAddress.type,
          addressLine1: normalizedAddress.line1,
          street: normalizedAddress.line1,
          streetAddress: normalizedAddress.line1,
          Street__c: normalizedAddress.line1,
          City__c: normalizedAddress.city,
          State__c: normalizedAddress.state,
          Country__c: normalizedAddress.country,
          postalCode: normalizedAddress.zipCode,
          Postal_Code__c: normalizedAddress.zipCode,
        };
      })
      .filter((address) => hasProfileRowValue(address, ["line1", "city", "state", "country", "zipCode"]));
    return {
      addresses,
      addressList: addresses,
    };
  }

  if (section === "insurance") {
    const deletedInsuranceRelatedIds = (payload.deletedInsuranceRelatedIds || [])
      .map(getCrmRecordId)
      .filter(Boolean);
    const insuranceRelated = (payload.insuranceRelated || payload.insuranceDependents || [])
      .map((dependent) => {
        const id = getCrmRecordId(dependent.id || dependent.Id);
        const normalizedDependent = {
          ...(id ? { id } : {}),
          name: trimStringValue(dependent.name || dependent.fullName || dependent.dependentName),
          relationship: trimStringValue(dependent.relationship) || "Other",
          dateOfBirth: cleanOptionalDate(dependent.dateOfBirth || dependent.dob),
          coverage: trimStringValue(dependent.coverage || dependent.insuranceType) || "Health Insurance",
          nominee: normalizeProfileBoolean(dependent.nominee),
          phone: trimStringValue(dependent.phone),
          email: trimStringValue(dependent.email),
        };
        return {
          ...normalizedDependent,
          fullName: normalizedDependent.name,
          dependentName: normalizedDependent.name,
          Relationship__c: normalizedDependent.relationship,
          Date_of_Birth__c: normalizedDependent.dateOfBirth,
          Insurance_Coverage__c: normalizedDependent.coverage,
          Primary_Emergency__c: normalizedDependent.nominee,
        };
      })
      .filter((dependent) => hasProfileRowValue(dependent, ["name", "dateOfBirth"]));
    return {
      insuranceRelated,
      insuranceDependents: insuranceRelated,
      dependents: insuranceRelated,
      deletedInsuranceRelatedIds,
      insuranceRelatedDeletedIds: deletedInsuranceRelatedIds,
      deletedRelatedContactIds: deletedInsuranceRelatedIds,
    };
  }

  if (section === "payroll") {
    const payrollIdentifiers = (payload.payrollIdentifiers || [])
      .map((item) => ({
        label: trimStringValue(item.label),
        key: trimStringValue(item.key || item.label),
        value: trimStringValue(item.value),
      }))
      .filter((item) => item.label || item.key);
    return { payrollIdentifiers };
  }

  if (section === "bank") {
    const bankAccounts = (payload.bankAccounts || [])
      .map((account) => {
        const id = getCrmRecordId(account.id || account.Id);
        const status = trimStringValue(account.status) || "Active";
        const normalizedAccount = {
          ...(id ? { id } : {}),
          bankName: trimStringValue(account.bankName || account.bank),
          accountHolder: trimStringValue(account.accountHolder || account.holderName),
          accountNumber: trimStringValue(account.accountNumber || account.bankAccount || account.account),
          ifsc: trimStringValue(account.ifsc || account.ifscCode),
          accountType: trimStringValue(account.accountType || account.type) || "Salary",
          status,
          primary: status.toLowerCase() === "primary" || account.primary === true,
        };
        return {
          ...normalizedAccount,
          bank: normalizedAccount.bankName,
          holderName: normalizedAccount.accountHolder,
          bankAccount: normalizedAccount.accountNumber,
          ifscCode: normalizedAccount.ifsc,
          type: normalizedAccount.accountType,
          Bank_Name__c: normalizedAccount.bankName,
          Account_Holder_Name__c: normalizedAccount.accountHolder,
          Bank_Account_Number__c: normalizedAccount.accountNumber,
          IFSC_Code__c: normalizedAccount.ifsc,
          Type__c: normalizedAccount.accountType,
        };
      })
      .filter((account) => hasProfileRowValue(account, ["bankName", "accountHolder", "accountNumber", "ifsc"]));
    return {
      bankAccounts,
      bankDetails: bankAccounts,
    };
  }

  return payload;
};

const normalizeProjectCreatePayload = (payload = {}) => {
  const title = trimStringValue(payload.title || payload.name || payload.projectName);
  const projectManagerId = getCrmRecordId(payload.projectManagerId || payload.Project_Manager__c);
  const accountId = getCrmRecordId(payload.accountId || payload.AccountId || payload.Account__c);
  const color = trimStringValue(payload.color || payload.colour);
  const savePayload = {
    title,
    name: title,
    description: trimStringValue(payload.description),
    projectManagerId,
    Project_Manager__c: projectManagerId,
    accountId,
    active: true,
  };

  if (/^#[0-9a-fA-F]{6}$/.test(color)) {
    savePayload.color = color.toUpperCase();
    savePayload.colour = color.toUpperCase();
    savePayload.Color__c = color.toUpperCase();
  }

  return savePayload;
};

const normalizeProjectMentionValues = (mentions = []) =>
  (Array.isArray(mentions) ? mentions : [])
    .map((mention) =>
      getCrmRecordId(mention?.contactId || mention?.employeeSalesforceId || mention?.id) ||
      trimStringValue(mention?.email || mention)
    )
    .filter(Boolean);

const normalizeProjectEntityPayload = (payload = {}) => {
  const projectId = getCrmRecordId(payload.projectId || payload.Project__c);
  const assignedToId = getCrmRecordId(payload.assignedToId || payload.Assigned_To__c);
  const estimatedHours = cleanEstimatedHours(payload.estimatedHours ?? payload.Estimated_Hours__c);
  const dueDate = cleanOptionalDate(payload.dueDate || payload.Due_Date__c);
  const savePayload = {
    projectId,
    Project__c: projectId,
    title: trimStringValue(payload.title),
    Title__c: trimStringValue(payload.title),
    assignedToId,
    Assigned_To__c: assignedToId || null,
    assignedTo: trimStringValue(payload.assignedTo),
    priority: trimStringValue(payload.priority) || "Medium",
    Priority__c: trimStringValue(payload.priority) || "Medium",
    status: trimStringValue(payload.status) || "Defined",
    Status__c: trimStringValue(payload.status) || "Defined",
    description: trimStringValue(payload.description),
    Description__c: trimStringValue(payload.description),
    ...(payload.inlineFileIds?.length ? { inlineFileIds: payload.inlineFileIds } : {}),
  };
  const descriptionMentions = normalizeProjectMentionValues(payload.descriptionMentions || payload.mentions);

  if (dueDate) {
    savePayload.dueDate = dueDate;
    savePayload.Due_Date__c = dueDate;
  }
  if (estimatedHours !== "") {
    savePayload.estimatedHours = estimatedHours;
    savePayload.Estimated_Hours__c = estimatedHours;
  }
  if (descriptionMentions.length) {
    savePayload.descriptionMentions = descriptionMentions;
    savePayload.mentions = descriptionMentions;
  }

  return savePayload;
};

const normalizeProjectEntityUpdatePayload = (payload = {}) => {
  const normalized = normalizeProjectEntityPayload(payload);
  delete normalized.projectId;
  delete normalized.Project__c;
  return normalized;
};

const getProjectEntityPayloadError = (payload = {}, { requireProject = false, requireAssignee = false } = {}) => {
  if (requireProject && !payload.projectId) {
    return "Select a valid project before saving.";
  }
  if (!payload.title) {
    return "Project entity title is required.";
  }
  if (requireAssignee && !payload.assignedToId) {
    return "Select a valid assignee before saving.";
  }
  if (payload.estimatedHours === null) {
    return "Estimated hours must be a valid number.";
  }
  return "";
};

const PROJECT_ENTITY_FILE_MAX_BYTES = 10 * 1024 * 1024;
const PROJECT_ENTITY_BLOCKED_FILE_EXTENSIONS = new Set(["ade", "bat", "cmd", "com", "exe", "jar", "js", "msi", "ps1", "scr", "sh", "vbs"]);

const getFileValidationError = (file) => {
  if (!file) {
    return "Select a valid file.";
  }
  if (file.size > PROJECT_ENTITY_FILE_MAX_BYTES) {
    return `${file.name} is larger than 10 MB.`;
  }
  const extension = String(file.name || "").split(".").pop().toLowerCase();
  if (PROJECT_ENTITY_BLOCKED_FILE_EXTENSIONS.has(extension)) {
    return `${file.name} is not an allowed file type.`;
  }
  return "";
};

const unwrapApiData = (payload) => {
  if (!payload || typeof payload !== "object") {
    return payload;
  }
  if (payload.data !== undefined && payload.data !== null) {
    return payload.data;
  }
  if (payload.result !== undefined && payload.result !== null) {
    return payload.result;
  }
  return payload;
};

const getUploadUrlFromPayload = (payload) => {
  const data = unwrapApiData(payload) || {};
  return trimStringValue(
    data.uploadUrl ||
      data.signedUploadUrl ||
      data.presignedUploadUrl ||
      data.presignedUrl ||
      data.signedUrl ||
      data.url
  );
};

const getDownloadUrlFromPayload = (payload) => {
  const data = unwrapApiData(payload) || {};
  return trimStringValue(data.downloadUrl || data.contentDownloadUrl || data.publicUrl || data.fileUrl || data.signedUrl || data.url);
};

const getCreatedProjectEntityId = (createdEntity, normalizedEntities = []) => {
  const normalizedId = getCrmRecordId(normalizedEntities[0]?.id);
  if (normalizedId) {
    return normalizedId;
  }

  const data = unwrapApiData(createdEntity) || {};
  return (
    getCrmRecordId(createdEntity?.id) ||
    getCrmRecordId(createdEntity?.entityId) ||
    getCrmRecordId(data.id) ||
    getCrmRecordId(data.entityId) ||
    getCrmRecordId(data.entity?.id) ||
    getCrmRecordId(data.entity?.Id)
  );
};

const getFileHeaders = (headers, file) => {
  if (headers && typeof headers === "object" && !Array.isArray(headers)) {
    return headers;
  }
  return file.type ? { "Content-Type": file.type } : {};
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",").pop() : result);
    };
    reader.onerror = () => reject(new Error(`${file?.name || "File"} could not be read.`));
    reader.readAsDataURL(file);
  });

const buildWeekTimePayload = (entries = {}, monthId, week) => {
  const days = Object.entries(entries)
    .map(([dayKey, entry]) => {
      const workDate = entry?.workDate || getWorkDateForWeekDay(monthId, week, dayKey);
      const originalEntry = week?.entries?.[dayKey] || {};
      const deletedEntries = uniqueTimeDeleteItems([
        ...getDeletedNonApprovedTimeItems(entry, originalEntry, week, workDate),
        ...getExplicitDeletedNonApprovedTimeItems(entry, week, workDate),
      ]);
      const deletedEntryIds = deletedEntries.map((item) => item.id);
      const mutationStatus = getTimesheetMutationStatus(entry?.status || week?.status || "Draft");
      const items = getEntryItems(entry)
        .filter(hasTimeItemInput)
        .filter((item) => !isReadOnlyTimeItem(item, item.status || entry?.status || week?.status || "Draft"))
        .map((item) => {
          const timeEntryId = getTimeEntryRecordId(item);
          return {
            id: timeEntryId,
            timeEntryId,
            recordId: timeEntryId,
            timeCardEntryId: timeEntryId,
            workDate,
            hours: Number(item.hours || 0),
            projectId: getCrmRecordId(item.projectId),
            projectName: trimStringValue(item.project),
            taskId: getCrmRecordId(item.taskId),
            taskName: trimStringValue(item.task),
            taskTitle: trimStringValue(item.task),
            description: trimStringValue(item.description),
            status: getTimesheetMutationStatus(item.status || mutationStatus),
          };
        });
      const totalHours = items.reduce((sum, item) => sum + Number(item.hours || 0), 0);
      const keptEntryIds = items.map((item) => item.id).filter(Boolean);
      return {
        dayKey,
        workDate,
        status: mutationStatus,
        operation: "replace-draft",
        replaceDateEntries: true,
        replaceWorkDateEntries: true,
        replaceNonApprovedEntries: true,
        deleteMissingDraftEntries: true,
        deleteMissingNonApprovedEntries: true,
        hardDeleteNonApprovedEntries: true,
        keepEntryIds: keptEntryIds,
        keptEntryIds,
        items,
        totalHours,
        dayTotalHours: totalHours,
        deletedEntryIds,
        deleteEntryIds: deletedEntryIds,
        deletedEntries,
      };
    })
    .filter((day) => day && isIsoDateValue(day.workDate) && (day.items.length || day.deletedEntryIds.length));
  const deletedEntries = days.flatMap((day) => day.deletedEntries || []);
  const deletedEntryIds = deletedEntries.map((entry) => entry.id);

  return {
    month: monthId,
    weekId: week?.id || "",
    operation: "replace-draft",
    replaceWorkDates: days.map((day) => day.workDate),
    replaceDateEntries: true,
    replaceWorkDateEntries: true,
    replaceNonApprovedEntries: true,
    deleteMode: "hard-delete",
    replaceDraftEntries: true,
    deleteMissingDraftEntries: true,
    deleteMissingNonApprovedEntries: true,
    hardDeleteNonApprovedEntries: true,
    deletedEntries,
    deletedEntryIds,
    deleteEntryIds: deletedEntryIds,
    hardDeleteEntries: deletedEntries,
    hardDeleteEntryIds: deletedEntryIds,
    days,
  };
};

const getSavedTimePayloadWorkDates = (payload = {}) =>
  new Set(
    (payload.days || [])
      .map((day) => day.workDate)
      .filter(Boolean)
  );

const markTimesheetEntryAsSavedDraft = (entry = {}) => {
  const entryStatus = entry.status || "Draft";
  const items = getEntryItems(entry).map((item) => {
    const itemStatus = item.status || entryStatus;
    return !isReadOnlyTimeItem(item, itemStatus)
      ? { ...item, status: "Draft", reviewComment: "" }
      : item;
  });

  return {
    ...entry,
    status: "Draft",
    reviewComment: "",
    items,
  };
};

const markSavedTimesheetDatesAsDraft = (month, workDates) => {
  if (!month || !workDates?.size) {
    return month;
  }

  return {
    ...month,
    status: "Draft",
    weeks: month.weeks.map((week) => {
      let touched = false;
      const entries = Object.fromEntries(
        Object.entries(week.entries).map(([dayId, entry]) => {
          const workDate = entry?.workDate || getWorkDateForWeekDay(month.id, week, dayId);
          if (!workDates.has(workDate)) {
            return [dayId, entry];
          }
          touched = true;
          return [dayId, { ...markTimesheetEntryAsSavedDraft(entry), workDate }];
        })
      );
      return touched ? { ...week, status: "Draft", entries } : week;
    }),
  };
};

const getWeekTimePayloadError = (payload) => {
  const days = payload?.days || [];
  if (!days.length) {
    return "Add at least one time entry before saving.";
  }

  const invalidDay = days.find((day) =>
    (day.items || []).some((item) => {
      const hours = Number(item.hours || 0);
      return (
        !Number.isFinite(hours) ||
        hours <= 0 ||
        hours > REQUIRED_DAILY_TIMESHEET_HOURS ||
        !String(item.projectName || "").trim() ||
        !String(item.description || "").trim() ||
        (String(item.taskName || "").trim() && !String(item.taskId || "").trim())
      );
    })
  );

  if (invalidDay) {
    return "Each saved time row needs project, description, 1-8 hours, and any selected entity must belong to that project.";
  }

  const overLimitDay = days.find((day) =>
    (day.items || []).reduce((sum, item) => sum + Number(item.hours || 0), 0) > REQUIRED_DAILY_TIMESHEET_HOURS
  );
  if (overLimitDay) {
    return `Daily timesheet total cannot exceed ${REQUIRED_DAILY_TIMESHEET_HOURS} hours.`;
  }

  return "";
};

const getSelectedTimesheetSubmitError = (cellIds = [], monthId, monthTimesheet = [], selectedMonth) => {
  const invalidEntries = cellIds
    .map((cellId) => {
      const [, weekId, dayKey] = cellId.split(":");
      const week = monthTimesheet.find((monthWeek) => monthWeek.id === weekId);
      const entry = week?.entries?.[dayKey];
      const workDate = entry?.workDate || getWorkDateForWeekDay(monthId, week, dayKey);
      const status = getTimesheetEntryStatus(entry, selectedMonth);
      if (!week || !workDate || entry?.isOutsideMonth || isLockedTimeStatus(status)) {
        return null;
      }

      const hours = getEntryHours(entry);
      return hours === REQUIRED_DAILY_TIMESHEET_HOURS
        ? null
        : `${workDate} (${Number(hours.toFixed(2)) || 0}h)`;
    })
    .filter(Boolean);

  return invalidEntries.length
    ? `Submit requires exactly ${REQUIRED_DAILY_TIMESHEET_HOURS} hours on each selected date. Fix ${invalidEntries.slice(0, 3).join(", ")}${invalidEntries.length > 3 ? "..." : ""}.`
    : "";
};

const formatTimesheetSubmitDate = (dateValue) => {
  if (!dateValue) {
    return "Selected date";
  }
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return dateValue;
  }
  return date.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const firstResponseValue = (source, keys, fallback = undefined) => {
  for (const key of keys) {
    const value = key.split(".").reduce((current, item) => current?.[item], source);
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return fallback;
};

const getSubmitResultNumber = (result, keys) => {
  const value = firstResponseValue(result, keys, 0);
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const buildTimesheetSubmitMessage = (result, selectedCount) => {
  const skippedCount = getSubmitResultNumber(result, ["skipped", "skippedCount", "data.skipped", "data.skippedCount"]);
  const failedCount = getSubmitResultNumber(result, ["failed", "failedCount", "errorCount", "data.failed", "data.failedCount", "data.errorCount"]);
  const submittedCount = getSubmitResultNumber(result, ["submitted", "submittedCount", "successCount", "data.submitted", "data.submittedCount", "data.successCount"]);
  const issueCount = skippedCount + failedCount;

  if (issueCount > 0) {
    const confirmedCount = submittedCount || Math.max(selectedCount - issueCount, 0);
    return `${confirmedCount} submitted. ${issueCount} ${issueCount === 1 ? "entry needs" : "entries need"} attention.`;
  }

  return `${selectedCount} timesheet ${selectedCount === 1 ? "box" : "boxes"} submitted for approval.`;
};

const normalizeForms = (payload) => {
  const data = payload?.data || payload || {};
  const forms = Array.isArray(data.forms) ? data.forms : Array.isArray(payload) ? payload : [];
  return forms.map((form) => ({
    id: form.id || form.formId || "appraisal",
    title: form.title || form.name || "Appraisal Form",
    type: form.type || "Appraisal Form",
    description: form.description || "",
    dueText: form.dueText || "Closed",
    status: form.status || (form.submitted ? "Submitted" : "Open"),
    active: form.active !== false && !form.submitted,
    requiresAction: Boolean(form.requiresAction),
    submitted: Boolean(form.submitted),
    submittedAt: form.submittedAt || "",
    submissionId: form.submissionId || "",
  }));
};

const NewHrmApp = () => {
  const initialPortalRouteRef = useRef(getInitialPortalRoute());
  const [activeAction, setActiveAction] = useState("");
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [activeNav, setActiveNav] = useState(initialPortalRouteRef.current.nav);
  const [requests, setRequests] = useState([]);
  const [workerRequests, setWorkerRequests] = useState([]);
  const [attendance, setAttendance] = useState({
    status: "checked-out",
    checkInTime: "",
    checkInAt: "",
    checkOutTime: "",
    date: getAttendanceDate(),
    loggedDate: getAttendanceLogDate(),
    logs: [],
  });
  const [attendanceNetworkAccess, setAttendanceNetworkAccess] = useState({
    allowed: false,
    message: "Checking office network.",
    resolved: false,
  });
  const [dataLoading, setDataLoading] = useState(false);
  const [dataScope, setDataScope] = useState("");
  const [pendingResources, setPendingResources] = useState([]);
  const [resourceErrors, setResourceErrors] = useState({});
  const [timeDataLoading, setTimeDataLoading] = useState(false);
  const [timeDataError, setTimeDataError] = useState("");
  const [dataError, setDataError] = useState("");
  const [assetAllocations, setAssetAllocations] = useState([]);
  const [defaulterLeaderboard, setDefaulterLeaderboard] = useState({ title: "HRM Defaulter", summary: {}, defaulters: [] });
  const [expenses, setExpenses] = useState([]);
  const [directoryPeople, setDirectoryPeople] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveCredits, setLeaveCredits] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [managedUsers, setManagedUsers] = useState([]);
  const [onboardingTasks, setOnboardingTasks] = useState(initialOnboardingTasks);
  const [payrollSlips, setPayrollSlips] = useState([]);
  const [policyRows, setPolicyRows] = useState([]);
  const [formRows, setFormRows] = useState([]);
  const [profile, setProfile] = useState(EMPTY_EMPLOYEE_PROFILE);
  const [projectEntities, setProjectEntities] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [allocatedProjectOptions, setAllocatedProjectOptions] = useState([]);
  const [taskOptions, setTaskOptions] = useState([]);
  const [searchTerm] = useState("");
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestDetailLoading, setRequestDetailLoading] = useState(false);
  const [selectedManagedUserId, setSelectedManagedUserId] = useState("");
  const [selectedTimesheetMonthId, setSelectedTimesheetMonthId] = useState(initialTimesheetMonths[0].id);
  const initialSelectedTimesheetDate = getInitialTimesheetDate(initialTimesheetMonths[0].id);
  const [selectedTimesheetDate, setSelectedTimesheetDate] = useState(initialSelectedTimesheetDate);
  const [selectedTimesheetCellIds, setSelectedTimesheetCellIds] = useState([]);
  const [selectedTimeWeekId, setSelectedTimeWeekId] = useState(
    getWeekForTimesheetDate(initialTimesheetMonths[0], initialSelectedTimesheetDate)?.id || initialTimesheetMonths[0].weeks[0].id
  );
  const [selectedTimeDayId, setSelectedTimeDayId] = useState(getDayIdForDate(initialSelectedTimesheetDate));
  const [timesheetMonths, setTimesheetMonths] = useState(initialTimesheetMonths);
  const [timesheetSubmitWarning, setTimesheetSubmitWarning] = useState(null);
  const [toast, setToast] = useState("");
  const [user, setUser] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [filterLoading, setFilterLoading] = useState("");
  const [operationLoading, setOperationLoading] = useState("");
  const [projectEntityDeepLinkId, setProjectEntityDeepLinkId] = useState(initialPortalRouteRef.current.entityId);
  const [requestDeepLinkId, setRequestDeepLinkId] = useState(initialPortalRouteRef.current.requestId);
  const [selectedEventId, setSelectedEventId] = useState(initialPortalRouteRef.current.eventId);
  const projectEntityFilterRequestRef = useRef(0);
  const hrmLoadRequestRef = useRef(0);
  const timeLoadRequestRef = useRef(0);
  const requestDetailRequestRef = useRef(0);
  const selectedTimesheetMonthIdRef = useRef(selectedTimesheetMonthId);
  selectedTimesheetMonthIdRef.current = selectedTimesheetMonthId;
  const profileViewUserIdRef = useRef("");
  const loadedProjectEntityDeepLinkRef = useRef("");
  const requestDeepLinkPrefersAssignedRef = useRef(Boolean(initialPortalRouteRef.current.prefersAssignedCases));
  const loadedRequestDeepLinkRef = useRef("");

  const selectedTimesheetMonth = useMemo(
    () => timesheetMonths.find((month) => month.id === selectedTimesheetMonthId) || timesheetMonths[0],
    [selectedTimesheetMonthId, timesheetMonths]
  );
  const monthTimesheet = useMemo(() => selectedTimesheetMonth?.weeks || [], [selectedTimesheetMonth]);
  const timesheetLockMessage = timeDataError || getTimesheetMonthLockMessage(selectedTimesheetMonth);
  const isTimesheetLocked = Boolean(timesheetLockMessage);
  const timeProjectOptions = useMemo(() => {
    if (allocatedProjectOptions.length) {
      return allocatedProjectOptions;
    }
    return getAllocatedTimeProjectOptions(projectOptions, taskOptions);
  }, [allocatedProjectOptions, projectOptions, taskOptions]);
  const selectedCurrentMonthCellIds = useMemo(
    () =>
      selectedTimesheetCellIds.filter((cellId) => {
        if (!cellId.startsWith(`${selectedTimesheetMonthId}:`)) {
          return false;
        }
        const [, weekId, dayId] = cellId.split(":");
        const week = monthTimesheet.find((monthWeek) => monthWeek.id === weekId);
        const entry = week?.entries?.[dayId];
        const workDate = entry?.workDate || getWorkDateForWeekDay(selectedTimesheetMonthId, week, dayId);
        const entryStatus = getTimesheetEntryStatus(entry, selectedTimesheetMonth);
        return Boolean(workDate) && !entry?.isOutsideMonth && !isLockedTimeStatus(entryStatus);
      }),
    [monthTimesheet, selectedTimesheetCellIds, selectedTimesheetMonth, selectedTimesheetMonthId]
  );

  const totalHours = useMemo(() => {
    const reportedTotalMinutes = Number(selectedTimesheetMonth?.totalMinutes);
    if (Number.isFinite(reportedTotalMinutes) && selectedTimesheetMonth?.totalMinutes !== "") {
      return formatMinutes(Math.round(reportedTotalMinutes));
    }

    const reportedTotalHours = formatHoursValue(selectedTimesheetMonth?.totalHours);
    if (reportedTotalHours) {
      return reportedTotalHours;
    }

    const totalMinutes = monthTimesheet.reduce(
      (monthTotal, week) =>
        monthTotal + Object.values(week.entries).reduce((weekTotal, entry) => weekTotal + parseEntryHoursToMinutes(entry), 0),
      0
    );
    return formatMinutes(totalMinutes);
  }, [monthTimesheet, selectedTimesheetMonth]);

  const selectedTimeWeek = useMemo(
    () => monthTimesheet.find((week) => week.id === selectedTimeWeekId) || monthTimesheet[0],
    [monthTimesheet, selectedTimeWeekId]
  );
  const isAdmin = ADMIN_EMAILS.has(String(user?.email || "").trim().toLowerCase());
  const viewUserId = isAdmin && selectedManagedUserId ? selectedManagedUserId : "";
  const viewScope = user ? `${user.id}:${viewUserId}` : "";
  const viewScopeRef = useRef(viewScope);
  viewScopeRef.current = viewScope;
  const requiredResources = SCREEN_RESOURCES[activeNav] || [];
  const needsTimesheet = ["dashboard", "time"].includes(activeNav);
  const screenDataLoading = Boolean(user) && (
    dataScope !== viewScope ||
    pendingResources.some((key) => requiredResources.includes(key)) ||
    (needsTimesheet && timeDataLoading)
  );
  const screenDataError = dataError || requiredResources.map((key) => resourceErrors[key]).find(Boolean) || (needsTimesheet ? timeDataError : "");
  profileViewUserIdRef.current = viewUserId;
  const viewedUser = useMemo(() => {
    if (!isAdmin || !selectedManagedUserId) {
      return user;
    }

    const selectedValue = String(selectedManagedUserId);
    const currentUserMatchesSelected = [user?.id, user?.contactId, user?.employeeSalesforceId, user?.email]
      .filter(Boolean)
      .map(String)
      .includes(selectedValue);
    if (currentUserMatchesSelected) {
      return user;
    }

    return managedUsers.find((managedUser) =>
      [managedUser.id, managedUser.contactId, managedUser.employeeSalesforceId, managedUser.email]
        .filter(Boolean)
        .map(String)
        .includes(selectedValue)
    ) || null;
  }, [isAdmin, managedUsers, selectedManagedUserId, user]);
  const actorCanCreateProject = PROJECT_CREATOR_EMAILS.has(String(user?.email || "").trim().toLowerCase());
  const canViewEvents = Boolean(authResolved && user);
  const viewedPermissionEmail = isAdmin && selectedManagedUserId ? viewedUser?.email : user?.email;
  const canViewMyCases = CASE_WORKER_EMAILS.has(String(viewedPermissionEmail || "").trim().toLowerCase());
  const requestViewUserId = viewUserId;
  const viewedCanCreateProject = PROJECT_CREATOR_EMAILS.has(String(viewedPermissionEmail || "").trim().toLowerCase());
  const effectiveLeaveBalances = useMemo(
    () => applyPendingLeaveReservations(leaveBalances, leaveRequests),
    [leaveBalances, leaveRequests]
  );

  const filteredPolicies = useMemo(() => {
    if (!searchTerm.trim()) {
      return policyRows;
    }

    const query = searchTerm.toLowerCase();
    return policyRows.filter((policy) => policy.title.toLowerCase().includes(query));
  }, [policyRows, searchTerm]);

  const loadManagedUsers = useCallback(async () => {
    if (!isAdmin) {
      setManagedUsers([]);
      return;
    }

    setAdminUsersLoading(true);
    try {
      const payload = await getManagedUsers({ page: 1, pageSize: 500 });
      const users = normalizeUsers(payload);
      setManagedUsers(users);
      setSelectedManagedUserId((current) => current || String(user?.id || users[0]?.id || ""));
    } finally {
      setAdminUsersLoading(false);
    }
  }, [isAdmin, user?.id]);

  useLayoutEffect(() => {
    // Do not expose the previous employee's records while View as is loading.
    setAttendance(normalizeAttendance({}, []));
    setProfile(EMPTY_EMPLOYEE_PROFILE);
    setRequests([]);
    setWorkerRequests([]);
    setPayrollSlips([]);
    setSelectedPayslip(null);
    setSelectedRequest(null);
    setRequestDetailLoading(false);
    setSelectedPolicy(null);
    setSelectedLeaveRequest(null);
    setSelectedExpense(null);
    setActiveAction("");
    setOperationLoading("");
    setExpenses([]);
    setDirectoryPeople([]);
    setLeaveBalances([]);
    setLeaveCredits([]);
    setLeaveRequests([]);
    setLeaveTypes([]);
    setHolidays([]);
    setPolicyRows([]);
    setFormRows([]);
    setProjectEntities([]);
    setProjectOptions([]);
    setAllocatedProjectOptions([]);
    setTaskOptions([]);
    setAssetAllocations([]);
    setTimesheetMonths(initialTimesheetMonths);
    setSelectedTimesheetCellIds([]);
    setResourceErrors({});
    setTimeDataError("");
    return () => {
      hrmLoadRequestRef.current += 1;
      timeLoadRequestRef.current += 1;
      projectEntityFilterRequestRef.current += 1;
      requestDetailRequestRef.current += 1;
    };
  }, [viewScope]);

  const loadHrmData = useCallback(async ({ preserveLeaveRequests = [] } = {}) => {
    if (!user || viewScopeRef.current !== viewScope) {
      return false;
    }

    const requestId = hrmLoadRequestRef.current + 1;
    hrmLoadRequestRef.current = requestId;
    const isCurrent = () => requestId === hrmLoadRequestRef.current && viewScopeRef.current === viewScope;
    setDataLoading(true);
    setDataScope(viewScope);
    setResourceErrors({});
    setDataError("");
    const currentYear = new Date().getFullYear();
    const resources = [
      ["attendance", () => Promise.all([getTodayAttendance(viewUserId), getAttendanceLogs(viewUserId, { includeOpen: true })]),
        ([today, logs]) => setAttendance(normalizeAttendance(today, logs)),
        () => setAttendance(normalizeAttendance({}, []))],
      ["balances", () => getLeaveBalances(viewUserId), (value) => setLeaveBalances(normalizeLeaveBalances(value)), () => setLeaveBalances([])],
      ["leaveCredits", () => getLeaveCredits(viewUserId), (value) => setLeaveCredits(normalizeLeaveCredits(value)), () => setLeaveCredits([])],
      ["leaveRequests", () => getLeaveRequests(viewUserId, { page: 1, pageSize: 500 }),
        (value) => setLeaveRequests(mergeLeaveRequests(normalizeLeaveRequests(value), preserveLeaveRequests)),
        () => setLeaveRequests((current) => preserveLeaveRequests.length ? mergeLeaveRequests(current, preserveLeaveRequests) : [])],
      ["leaveTypes", () => getLeaveTypes(viewUserId), (value) => setLeaveTypes(normalizeLeaveTypes(value)), () => setLeaveTypes([])],
      ["holidays", () => getHolidays(viewUserId, currentYear), (value) => setHolidays(normalizeHolidays(value)), () => setHolidays([])],
      ["payroll", () => getPayrollSlips(viewUserId), (value) => {
        const slips = normalizePayrollSlips(value);
        setPayrollSlips(slips);
        setSelectedPayslip((current) => slips.find((slip) => slip.id === current?.id) || slips[0] || null);
      }, () => { setPayrollSlips([]); setSelectedPayslip(null); }],
      ["policies", () => getPolicies(viewUserId), (value) => setPolicyRows(normalizePolicies(value)), () => setPolicyRows([])],
      ["forms", () => getForms(viewUserId), (value) => setFormRows(normalizeForms(value)), () => setFormRows([])],
      ["requests", () => getRequests(viewUserId), (value) => setRequests(normalizeRequests(value)), () => setRequests([])],
      ["workerRequests", () => canViewMyCases ? getRequests(viewUserId, { scope: "assigned" }) : Promise.resolve({ requests: [] }),
        (value) => setWorkerRequests(canViewMyCases ? normalizeRequests(value) : []), () => setWorkerRequests([])],
      ["leaderboard", () => getDefaulterLeaderboard(), (value) => setDefaulterLeaderboard(normalizeDefaulterLeaderboard(value)),
        () => setDefaulterLeaderboard({ title: "HRM Defaulter", summary: {}, defaulters: [] })],
      ["expenses", () => getExpenses(viewUserId), (value) => setExpenses(normalizeExpenses(value)), () => setExpenses([])],
      ["directory", () => getDirectory(viewUserId), (value) => setDirectoryPeople(normalizeDirectory(value)), () => setDirectoryPeople([])],
      ["profile", () => getProfile(viewUserId), (value) => setProfile(normalizeProfile(value, EMPTY_EMPLOYEE_PROFILE)), () => setProfile(EMPTY_EMPLOYEE_PROFILE)],
      ["projects", () => searchProjects(viewUserId), (value) => setProjectOptions(normalizeProjects(value)), () => setProjectOptions([])],
      ["allocatedProjects", () => searchProjects(viewUserId, "", { scope: "allocated", allocatedOnly: true }),
        (value) => setAllocatedProjectOptions(normalizeProjects(value)), () => setAllocatedProjectOptions([])],
      ["entities", () => Promise.allSettled([getProjectEntities(viewUserId), searchTasks(viewUserId)]), ([entities, tasks]) => {
        const rows = entities.status === "fulfilled" ? normalizeProjectEntities(entities.value) : [];
        setTaskOptions(tasks.status === "fulfilled" ? normalizeTasks(tasks.value) : []);
        setProjectEntities(rows.length ? rows : tasks.status === "fulfilled" ? normalizeProjectEntities(tasks.value) : []);
        if (entities.status === "rejected" && tasks.status === "rejected") throw entities.reason;
      }, () => { setProjectEntities([]); setTaskOptions([]); }],
      ["assets", () => getAssetAllocations(viewUserId), (value) => setAssetAllocations(normalizeAssetAllocations(value)), () => setAssetAllocations([])],
    ];

    setPendingResources(resources.map(([key]) => key));
    // Each screen can render as soon as its own resources settle.
    const results = await Promise.all(resources.map(async ([key, load, apply, clear]) => {
      try {
        const value = await load();
        if (isCurrent()) apply(value);
        return true;
      } catch (error) {
        if (isCurrent()) {
          clear();
          setResourceErrors((current) => ({ ...current, [key]: error.message || "Data could not be loaded. Please refresh." }));
        }
        return false;
      } finally {
        if (isCurrent()) setPendingResources((current) => current.filter((item) => item !== key));
      }
    }));

    if (!isCurrent()) return false;
    setFilterLoading((current) => current === "employee" ? "" : current);
    setDataLoading(false);
    return results.every(Boolean);
  }, [canViewMyCases, user, viewScope, viewUserId]);

  useEffect(() => {
    loadManagedUsers().catch((error) => {
      setDataError(error.message || "Admin users could not be loaded.");
    });
  }, [loadManagedUsers]);

  useEffect(() => {
    loadHrmData();
  }, [loadHrmData]);

  useEffect(() => {
    if (activeNav === "myCases" && !canViewMyCases) {
      setActiveNav("dashboard");
    }
  }, [activeNav, canViewMyCases]);

  useEffect(() => {
    // Preserve cookie-backed Events deep links until the authenticated user resolves.
    if (!authResolved || !user || activeNav !== "events" || canViewEvents) {
      return;
    }
    setActiveNav("dashboard");
    setSelectedEventId("");
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("event");
      url.searchParams.delete("eventId");
      url.searchParams.set("tab", "dashboard");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }
  }, [activeNav, authResolved, canViewEvents, user]);

  const refreshSelectedTimesheetMonth = useCallback(async ({ manageLoading = true, showToast = false, monthId = selectedTimesheetMonthIdRef.current } = {}) => {
    if (!user || viewScopeRef.current !== viewScope) return false;
    const requestId = ++timeLoadRequestRef.current;
    const isCurrent = () => requestId === timeLoadRequestRef.current && viewScopeRef.current === viewScope;
    setTimeDataLoading(true);
    setTimeDataError("");
    if (manageLoading) setOperationLoading("time-refresh");
    else setOperationLoading((current) => current === "time-refresh" ? "" : current);

    try {
      const payload = await getTimeMonth(viewUserId, monthId);
      if (!isCurrent()) return false;
      setTimesheetMonths((current) => {
        const fallbackMonth = createBlankTimesheetMonth(monthId);
        const month = normalizeTimesheetMonth(payload, monthId, fallbackMonth);
        return current.some((item) => item.id === month.id)
          ? current.map((item) => item.id === month.id ? month : item)
          : [month, ...current];
      });
      if (showToast) setToast("Timesheet refreshed.");
      return true;
    } catch (error) {
      if (!isCurrent()) return false;
      const message = error.message || "Timesheet could not be refreshed.";
      setTimeDataError(message);
      if (showToast) setToast(message);
      return false;
    } finally {
      if (isCurrent()) {
        setTimeDataLoading(false);
        if (manageLoading) setOperationLoading((current) => current === "time-refresh" ? "" : current);
        setFilterLoading((current) => current === "time" ? "" : current);
      }
    }
  }, [user, viewScope, viewUserId]);

  useEffect(() => {
    refreshSelectedTimesheetMonth({ manageLoading: false, monthId: selectedTimesheetMonthId });
  }, [refreshSelectedTimesheetMonth, selectedTimesheetMonthId]);

  const handleReloadHrmData = useCallback(async () => {
    if (!user) {
      return;
    }

    setOperationLoading("global-refresh");
    setSelectedTimesheetCellIds([]);
    setSelectedTimeDayId("");
    setDataError("");

    try {
      const [dataRefreshed, timeRefreshed] = await Promise.all([
        loadHrmData(),
        refreshSelectedTimesheetMonth({ manageLoading: false }),
        loadManagedUsers(),
      ]);
      if (viewScopeRef.current === viewScope) setToast(dataRefreshed && timeRefreshed ? "HRM data refreshed." : "Some HRM data could not be refreshed. Please try again.");
    } catch (error) {
      if (viewScopeRef.current === viewScope) setToast(error.message || "HRM data could not be refreshed.");
    } finally {
      if (viewScopeRef.current === viewScope) setOperationLoading((current) => current === "global-refresh" ? "" : current);
    }
  }, [loadHrmData, loadManagedUsers, refreshSelectedTimesheetMonth, user, viewScope]);

  const handleLogin = (session) => {
    if (!session?.user?.id || !session.user.email) {
      setToast("Sign-in could not be verified. Please sign in again.");
      return;
    }
    const nextUser = normalizeUser(session.user);
    localStorage.removeItem(AWC_ACCESS_TOKEN_KEY);
    localStorage.removeItem(AWC_TOKEN_EXPIRES_AT_KEY);
    localStorage.setItem(AWC_USER_KEY, JSON.stringify(nextUser));
    localStorage.setItem(SESSION_KEY, JSON.stringify(nextUser));
    setUser(nextUser);
    hrmLoadRequestRef.current += 1;
    setProfile(EMPTY_EMPLOYEE_PROFILE);
    setAuthResolved(true);
    setSelectedManagedUserId(String(nextUser.id || ""));
    setToast("Logged in to HRM.");
  };

  const handleLogout = async () => {
    try {
      await logoutFromHrm();
    } catch {
      // Local session cleanup is still valid for stateless JWT logout.
    }
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(AWC_ACCESS_TOKEN_KEY);
    localStorage.removeItem(AWC_TOKEN_EXPIRES_AT_KEY);
    localStorage.removeItem(AWC_USER_KEY);
    localStorage.removeItem("authTokenForGoogleLogin");
    localStorage.removeItem("contactUserId");
    localStorage.removeItem("contactUserName");
    setUser(null);
    hrmLoadRequestRef.current += 1;
    setProfile(EMPTY_EMPLOYEE_PROFILE);
    setAuthResolved(true);
    setToast("Logged out.");
  };

  useEffect(() => {
    const handleSessionExpired = () => {
      setUser(null);
      hrmLoadRequestRef.current += 1;
      setProfile(EMPTY_EMPLOYEE_PROFILE);
      setAuthResolved(true);
      setSelectedManagedUserId("");
      setOperationLoading("");
      setFilterLoading("");
      setToast("Session expired. Please sign in again.");
    };

    window.addEventListener("awc-auth-expired", handleSessionExpired);
    return () => window.removeEventListener("awc-auth-expired", handleSessionExpired);
  }, []);

  useEffect(() => {
    let active = true;
    getCurrentHrmSession()
      .then((session) => {
        if (!active) {
          return;
        }
        if (!session?.user) {
          localStorage.removeItem(SESSION_KEY);
          localStorage.removeItem(AWC_USER_KEY);
          setUser(null);
          return;
        }
        const nextUser = normalizeUser(session.user);
        localStorage.setItem(AWC_USER_KEY, JSON.stringify(nextUser));
        localStorage.setItem(SESSION_KEY, JSON.stringify(nextUser));
        setUser(nextUser);
        setSelectedManagedUserId(String(nextUser.id || ""));
      })
      .catch(() => {
        if (!active) {
          return;
        }
        localStorage.removeItem(SESSION_KEY);
        localStorage.removeItem(AWC_USER_KEY);
        setUser(null);
      })
      .finally(() => {
        if (active) {
          setAuthResolved(true);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setAttendanceNetworkAccess({
        allowed: false,
        message: "Checking office network.",
        resolved: false,
      });
      return undefined;
    }

    let active = true;
    const refreshAttendanceNetworkAccess = async () => {
      try {
        const response = await getHrmAccessStatus();
        if (!active) {
          return;
        }
        const access = response?.data || response || {};
        const allowed = access.attendanceMarkingAllowed === true;
        setAttendanceNetworkAccess({
          allowed,
          message: allowed
            ? ""
            : access.attendanceRestrictionMessage || "Attendance can only be marked from the Antrazal office network.",
          resolved: true,
        });
      } catch {
        if (active) {
          setAttendanceNetworkAccess({
            allowed: false,
            message: "Office network could not be verified. Attendance remains disabled until the network check succeeds.",
            resolved: true,
          });
        }
      }
    };
    const refreshWhenVisible = () => {
      if (!document.hidden) {
        refreshAttendanceNetworkAccess();
      }
    };

    refreshAttendanceNetworkAccess();
    const intervalId = window.setInterval(refreshAttendanceNetworkAccess, 15000);
    window.addEventListener("focus", refreshWhenVisible);
    window.addEventListener("online", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshWhenVisible);
      window.removeEventListener("online", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [user]);

  const handleActionOpen = (nextAction, record = null) => {
    setSelectedLeaveRequest(nextAction === "leave" ? record : null);
    setSelectedExpense(nextAction === "expense" ? record : null);
    setActiveAction(nextAction);
  };

  const handleActionClose = (nextAction) => {
    setActiveAction("");
    setSelectedLeaveRequest(null);
    setSelectedExpense(null);
    if (nextAction) {
      window.setTimeout(() => setActiveAction(nextAction), 0);
    }
  };

  const clearPortalDeepLinkState = useCallback((nextNav) => {
    setRequestDeepLinkId("");
    loadedRequestDeepLinkRef.current = "";
    requestDeepLinkPrefersAssignedRef.current = false;
    setSelectedEventId("");

    if (nextNav !== "requests" && nextNav !== "myCases") {
      requestDetailRequestRef.current += 1;
      setSelectedRequest(null);
      setRequestDetailLoading(false);
    }

    if (nextNav !== "projects") {
      setProjectEntityDeepLinkId("");
      loadedProjectEntityDeepLinkRef.current = "";
    }

    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      ["requestId", "caseId", "entityId", "projectEntityId", "ticketId", "projectId", "event", "eventId"].forEach((param) => {
        url.searchParams.delete(param);
      });
      url.searchParams.set("tab", nextNav === "myCases" ? "mycases" : nextNav === "myForms" ? "myforms" : nextNav);
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }
  }, []);

  const handleNavigate = (itemId) => {
    if (itemId === "myCases" && !canViewMyCases) {
      setToast("My Cases is only available for assigned HRM case workers.");
      return;
    }
    if (itemId === "events" && !canViewEvents) {
      setToast("Events is not available for this employee yet.");
      return;
    }
    clearPortalDeepLinkState(itemId);
    setActiveNav(itemId);
    setToast(`${itemId.charAt(0).toUpperCase()}${itemId.slice(1)} opened.`);
  };

  const handleOpenEvent = useCallback((eventId) => {
    if (!canViewEvents || eventId !== RETREAT_EVENT_ID) {
      setToast("This event is not available for this employee.");
      return;
    }
    setSelectedEventId(eventId);
    setActiveNav("events");
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", "events");
      url.searchParams.set("event", eventId);
      window.history.pushState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }
  }, [canViewEvents]);

  const handleCloseEvent = useCallback(() => {
    setSelectedEventId("");
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("event");
      url.searchParams.delete("eventId");
      url.searchParams.set("tab", "events");
      window.history.pushState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }
  }, []);

  useEffect(() => {
    const syncRouteFromUrl = () => {
      const route = getInitialPortalRoute();
      if (route.requestId) {
        requestDeepLinkPrefersAssignedRef.current = Boolean(route.prefersAssignedCases);
        setRequestDeepLinkId(route.requestId);
        setActiveNav(route.prefersAssignedCases && canViewMyCases ? "myCases" : "requests");
        return;
      }
      if (route.nav === "events") {
        setSelectedEventId(route.eventId);
        // Keep an authenticated Events deep link intact while the session user is resolving.
        setActiveNav(!authResolved || !user || canViewEvents ? "events" : "dashboard");
        return;
      }
      if (route.nav !== "dashboard" || route.entityId) {
        setActiveNav(route.nav);
      }
      if (route.entityId) {
        setProjectEntityDeepLinkId(route.entityId);
      }
    };

    window.addEventListener("popstate", syncRouteFromUrl);
    return () => window.removeEventListener("popstate", syncRouteFromUrl);
  }, [authResolved, canViewEvents, canViewMyCases, user]);

  const loadProjectEntityDeepLink = useCallback(async (entityId) => {
    const cleanEntityId = trimStringValue(entityId);
    if (!cleanEntityId) {
      return null;
    }

    const response = await getProjectEntity(viewUserId, cleanEntityId);
    const normalizedRows = normalizeProjectEntities(response);
    const matchedEntity =
      normalizedRows.find((entity) => projectEntityMatchesId(entity, cleanEntityId)) ||
      normalizedRows[0] ||
      null;

    if (matchedEntity?.id) {
      setProjectEntities((current) => mergeProjectEntityRows(current, [matchedEntity]));
    }

    return matchedEntity;
  }, [viewUserId]);

  useEffect(() => {
    if (!user || activeNav !== "projects") {
      return undefined;
    }

    const cleanEntityId = trimStringValue(projectEntityDeepLinkId);
    if (!cleanEntityId) {
      return undefined;
    }

    const loadKey = `${viewUserId || "self"}:${cleanEntityId}`;
    if (loadedProjectEntityDeepLinkRef.current === loadKey) {
      return undefined;
    }

    loadedProjectEntityDeepLinkRef.current = loadKey;
    let isActive = true;
    loadProjectEntityDeepLink(cleanEntityId).catch((error) => {
      if (isActive) {
        setToast(error.message || "Project entity link could not be opened.");
      }
    });

    return () => {
      isActive = false;
    };
  }, [activeNav, loadProjectEntityDeepLink, projectEntityDeepLinkId, user, viewUserId]);

  const loadRequestDeepLink = useCallback(async (requestId) => {
    const cleanRequestId = trimStringValue(requestId);
    if (!cleanRequestId) {
      return null;
    }

    const requestGeneration = ++requestDetailRequestRef.current;
    const detailResponse = await getRequest(requestViewUserId, cleanRequestId);
    if (requestGeneration !== requestDetailRequestRef.current || viewScopeRef.current !== viewScope) return null;
    const [detail] = normalizeRequests(detailResponse);
    if (!detail?.id) {
      return null;
    }

    const mergeRequest = (current) =>
      current.some((item) => item.id === detail.id)
        ? current.map((item) => (item.id === detail.id ? { ...item, ...detail } : item))
        : [detail, ...current];

    setSelectedRequest(detail);
    setRequests(mergeRequest);
    if (canViewMyCases) {
      setWorkerRequests(mergeRequest);
    }

    return detail;
  }, [canViewMyCases, requestViewUserId, viewScope]);

  useEffect(() => {
    if (!user || !requestDeepLinkId) {
      return undefined;
    }

    const targetNav = requestDeepLinkPrefersAssignedRef.current && canViewMyCases ? "myCases" : "requests";
    if (activeNav !== targetNav) {
      setActiveNav(targetNav);
      return undefined;
    }

    const cleanRequestId = trimStringValue(requestDeepLinkId);
    const loadKey = `${requestViewUserId || "self"}:${cleanRequestId}`;
    if (!cleanRequestId || loadedRequestDeepLinkRef.current === loadKey) {
      return undefined;
    }

    loadedRequestDeepLinkRef.current = loadKey;
    let isActive = true;
    setRequestDetailLoading(true);
    loadRequestDeepLink(cleanRequestId)
      .catch((error) => {
        if (isActive) {
          setToast(error.message || "Case link could not be opened.");
        }
      })
      .finally(() => {
        if (isActive) {
          setRequestDetailLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [activeNav, canViewMyCases, loadRequestDeepLink, requestDeepLinkId, requestViewUserId, user]);

  const handleDownloadPayslip = async (payslip) => {
    setOperationLoading("payroll-download");
    try {
      const payslipDocument = payslip?.period ? payslip : selectedPayslip || payrollSlips[0];
      if (!payslipDocument?.id) {
        setToast("No posted salary slip is available yet.");
        return;
      }

      const download = await downloadPayrollSlip(viewUserId, payslipDocument.id);
      if (!download?.blob) {
        throw new Error("Salary slip download is not available.");
      }

      const safePeriod = trimStringValue(payslipDocument.periodKey || payslipDocument.period || "latest")
        .replace(/[^a-zA-Z0-9_-]+/g, "_");
      const fileName = trimStringValue(download.fileName) || `Salary_Slip_${safePeriod}.pdf`;
      const objectUrl = URL.createObjectURL(download.blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      setToast(`${payslipDocument.period} salary slip downloaded.`);
    } catch (error) {
      setToast(error.message || "Salary slip download is not available.");
    } finally {
      setOperationLoading("");
    }
  };

  const handleSubmitLeave = async ({ requestId, description, duration, durationType, endDate, leaveType, oooAdded, startDate }) => {
    const leaveDays = Number(duration || 0);
    const cleanDescription = trimStringValue(description);
    const cleanLeaveType = trimStringValue(leaveType);
    const cleanDurationType = trimStringValue(durationType) || (leaveDays === 0.5 ? "Half Day" : "Full Day");
    if (!isIsoDateValue(startDate) || !isIsoDateValue(endDate) || !leaveDays || !cleanDescription || !cleanLeaveType || oooAdded !== true) {
      setToast("Leave details and the OOO calendar confirmation are required.");
      return false;
    }
    if (cleanDurationType.toLowerCase().includes("half") && startDate !== endDate) {
      setToast("Half-day leave can be applied for one date only.");
      return false;
    }

    const weekendConflicts = getLeaveWeekendConflicts(startDate, endDate);
    if (weekendConflicts.length) {
      setToast(formatLeaveWeekendToast(weekendConflicts));
      return false;
    }

    const holidayConflicts = getLeaveHolidayConflicts(startDate, endDate, holidays);
    if (holidayConflicts.length) {
      setToast(formatLeaveHolidayToast(holidayConflicts));
      return false;
    }

    const otherLeaveRequests = requestId
      ? leaveRequests.filter((request) => String(request.id) !== String(requestId))
      : leaveRequests;
    const overlapConflicts = getLeaveOverlapConflicts(startDate, endDate, otherLeaveRequests, leaveDays);
    if (overlapConflicts.length) {
      setToast(formatLeaveOverlapToast(overlapConflicts));
      return false;
    }

    const selectedLeaveBalance = effectiveLeaveBalances.find((balance) =>
      normalizeLeaveTypeKey(balance?.label || balance?.leaveType || balance?.type) === normalizeLeaveTypeKey(cleanLeaveType)
    );
    const isLossOfPay = normalizeLeaveTypeKey(cleanLeaveType) === "lop" || normalizeLeaveTypeKey(cleanLeaveType).includes("loss of pay");
    if (!isLossOfPay && selectedLeaveBalance) {
      const currentRequest = requestId
        ? leaveRequests.find((request) => String(request.id) === String(requestId))
        : null;
      const isSameLeaveType = normalizeLeaveTypeKey(currentRequest?.leaveType) === normalizeLeaveTypeKey(cleanLeaveType);
      const currentReservedDays = isSameLeaveType ? numericLeaveDays(currentRequest) : 0;
      const availableDays = Number(selectedLeaveBalance.remaining || 0) + currentReservedDays;
      if (leaveDays > availableDays) {
        const pendingReserved = Math.max(Number(selectedLeaveBalance.pendingReserved || 0) - currentReservedDays, 0);
        const pendingText = pendingReserved > 0 ? ` after ${pendingReserved} days already pending` : "";
        setToast(`Only ${availableDays} days available for ${cleanLeaveType}${pendingText}.`);
        return false;
      }
    }

    setOperationLoading("leave-save");
    try {
      const payload = {
        startDate,
        endDate,
        duration: cleanDurationType,
        durationType: cleanDurationType,
        leaveDays,
        leaveType: cleanLeaveType,
        reason: cleanDescription,
        description: cleanDescription,
        oooAdded: true,
      };
      let submittedLeaveRequest = null;
      if (requestId) {
        await updateLeaveRequest(viewUserId, requestId, payload);
      } else {
        const createResponse = await createLeaveRequest(viewUserId, payload);
        if (viewScopeRef.current !== viewScope) return false;
        [submittedLeaveRequest] = normalizeLeaveRequests(createResponse);
        if (!submittedLeaveRequest) {
          [submittedLeaveRequest] = normalizeLeaveRequests({
            data: {
              ...payload,
              id: `submitted-leave-${Date.now()}`,
              requestNumber: "Submitted leave",
              status: "Pending Approval",
              canEdit: false,
              canCancel: false,
            },
          });
        }
        if (submittedLeaveRequest) {
          setLeaveRequests((current) => mergeLeaveRequests([submittedLeaveRequest], current));
        }
      }
      await loadHrmData({ preserveLeaveRequests: submittedLeaveRequest ? [submittedLeaveRequest] : [] });
      if (viewScopeRef.current !== viewScope) return false;
      setToast(requestId ? "Leave request updated." : "Leave request submitted.");
      setActiveAction("");
      setSelectedLeaveRequest(null);
      return true;
    } catch (error) {
      if (viewScopeRef.current === viewScope) setToast(error.message || "Leave request could not be saved.");
      return false;
    } finally {
      if (viewScopeRef.current === viewScope) setOperationLoading((current) => current === "leave-save" ? "" : current);
    }
  };

  const handleCancelLeave = async (requestId) => {
    if (!requestId) {
      return false;
    }
    if (!window.confirm("Cancel this leave request? This action cannot be undone.")) {
      return false;
    }

    setOperationLoading("leave-cancel");
    try {
      await cancelLeaveRequest(viewUserId, requestId);
      await loadHrmData();
      if (viewScopeRef.current !== viewScope) return false;
      setToast("Leave request cancelled.");
      setActiveAction("");
      setSelectedLeaveRequest(null);
      return true;
    } catch (error) {
      if (viewScopeRef.current === viewScope) setToast(error.message || "Leave request could not be cancelled.");
      return false;
    } finally {
      if (viewScopeRef.current === viewScope) setOperationLoading((current) => current === "leave-cancel" ? "" : current);
    }
  };

  const handleSubmitTime = async ({ entries, weekId }) => {
    if (isTimesheetLocked) {
      setToast(timesheetLockMessage);
      setActiveAction("");
      return false;
    }

    setOperationLoading("time-save");
    try {
      const selectedWeek = monthTimesheet.find((week) => week.id === weekId);
      const payload = buildWeekTimePayload(entries, selectedTimesheetMonthId, selectedWeek);
      const payloadError = getWeekTimePayloadError(payload);
      if (payloadError) {
        setToast(payloadError);
        return false;
      }

      await saveTimeWeek(viewUserId, selectedTimesheetMonthId, weekId, payload);
      if (viewScopeRef.current !== viewScope) return false;
      const savedWorkDates = getSavedTimePayloadWorkDates(payload);
      setTimesheetMonths((current) =>
        current.map((month) =>
          month.id === selectedTimesheetMonthId
            ? markSavedTimesheetDatesAsDraft(month, savedWorkDates)
            : month
        )
      );
      const refreshed = await refreshSelectedTimesheetMonth({ manageLoading: false });
      if (viewScopeRef.current !== viewScope) return false;
      if (!refreshed) {
        setTimesheetMonths((current) =>
          current.map((month) =>
            month.id === selectedTimesheetMonthId
              ? markSavedTimesheetDatesAsDraft({
                  ...month,
                  weeks: month.weeks.map((week) => (week.id === weekId ? { ...week, entries } : week)),
                }, savedWorkDates)
              : month
          )
        );
      }
      setToast(refreshed ? "Week time entries saved." : "Week saved, but the calendar could not be refreshed.");
      setActiveAction("");
      return true;
    } catch (error) {
      if (viewScopeRef.current === viewScope) setToast(error.message || "Week time entries could not be saved.");
      return false;
    } finally {
      if (viewScopeRef.current === viewScope) setOperationLoading((current) => current === "time-save" ? "" : current);
    }
  };

  const handleTimesheetMonthChange = (monthId) => {
    const nextMonth = timesheetMonths.find((month) => month.id === monthId) || timesheetMonths[0];
    if (nextMonth.id !== selectedTimesheetMonthId) {
      setFilterLoading("time");
    }
    const nextSelectedDate = getInitialTimesheetDate(nextMonth.id);
    const nextWeek = getWeekForTimesheetDate(nextMonth, nextSelectedDate);
    setSelectedTimesheetMonthId(nextMonth.id);
    setSelectedTimesheetDate(nextSelectedDate);
    setSelectedTimesheetCellIds([]);
    setSelectedTimeWeekId(nextWeek?.id || nextMonth.weeks[0]?.id || "");
    setSelectedTimeDayId(getDayIdForDate(nextSelectedDate));
  };

  const handleManagedUserChange = (employeeId) => {
    const nextEmployeeId = String(employeeId || "");
    if (nextEmployeeId !== selectedManagedUserId) {
      setFilterLoading("employee");
      hrmLoadRequestRef.current += 1;
      profileViewUserIdRef.current = nextEmployeeId;
      setDataScope("");
      setDataError("");
      setProfile(EMPTY_EMPLOYEE_PROFILE);
    }
    setSelectedManagedUserId(nextEmployeeId);
  };

  const handleAssetsLoaded = useCallback((nextAssets) => {
    setAssetAllocations(nextAssets);
  }, []);

  const handleProjectEntityFiltersChange = useCallback(async (filters = {}) => {
    if (!user) {
      return false;
    }

    const requestId = projectEntityFilterRequestRef.current + 1;
    projectEntityFilterRequestRef.current = requestId;
    const assignedToIds = (Array.isArray(filters.assignedToIds) ? filters.assignedToIds : [filters.assignedToId])
      .map(getCrmRecordId)
      .filter(Boolean);
    const assignedToEmails = (Array.isArray(filters.assignedToEmails) ? filters.assignedToEmails : [filters.assignedToEmail])
      .map(trimStringValue)
      .filter(Boolean);
    const assignedToNames = (Array.isArray(filters.assignedToNames) ? filters.assignedToNames : [filters.assignedToName])
      .map(trimStringValue)
      .filter(Boolean);
    const projectIds = (Array.isArray(filters.projectIds) ? filters.projectIds : [filters.projectId])
      .map(getCrmRecordId)
      .filter(Boolean);
    const priorities = (Array.isArray(filters.priorities) ? filters.priorities : [filters.priority])
      .map(trimStringValue)
      .filter(Boolean);
    const search = trimStringValue(filters.search);
    const assignedToQueryValues = assignedToIds.length || assignedToEmails.length || assignedToNames.length
      ? [
          ...assignedToIds.map((assignedToId) => ({ assignedToId })),
          ...assignedToEmails.map((assignedToEmail) => ({ assignedToEmail })),
          ...assignedToNames.map((assignedToName) => ({ assignedToName })),
        ]
      : [{}];
    const projectQueryValues = projectIds.length ? projectIds : [""];
    const priorityQueryValues = priorities.length ? priorities : [""];

    setOperationLoading("project-filter");
    try {
      const requestFilters = [];
      assignedToQueryValues.forEach((assignedToFilter) => {
        projectQueryValues.forEach((projectId) => {
          priorityQueryValues.forEach((priority) => {
            requestFilters.push({
              ...assignedToFilter,
              priority,
              projectId,
              search,
            });
          });
        });
      });
      const dedupedFilters = Array.from(
        new Map(requestFilters.map((requestFilter) => [JSON.stringify(requestFilter), requestFilter])).values()
      );
      const payloads = await Promise.all(
        dedupedFilters.map((requestFilter) =>
          getProjectEntities(viewUserId, {
            assignedToId: requestFilter.assignedToId,
            assignedToEmail: requestFilter.assignedToEmail,
            assignedToName: requestFilter.assignedToName,
            priority: requestFilter.priority,
            projectId: requestFilter.projectId,
            search: requestFilter.search,
          })
        )
      );
      const entitiesById = new Map();
      payloads.flatMap((payload) => normalizeProjectEntities(payload)).forEach((entity) => {
        entitiesById.set(entity.id, entity);
      });

      if (projectEntityFilterRequestRef.current === requestId) {
        setProjectEntities((current) => {
          const nextRows = Array.from(entitiesById.values());
          const focusedEntity = current.find((entity) => projectEntityMatchesId(entity, projectEntityDeepLinkId));
          return focusedEntity ? mergeProjectEntityRows(nextRows, [focusedEntity]) : nextRows;
        });
      }
      return true;
    } catch (error) {
      if (projectEntityFilterRequestRef.current === requestId) {
        setToast(error.message || "Project entities could not be loaded.");
      }
      return false;
    } finally {
      if (projectEntityFilterRequestRef.current === requestId) {
        setOperationLoading("");
      }
    }
  }, [projectEntityDeepLinkId, user, viewUserId]);

  const handleTimesheetDateSelect = (dateValue, shouldOpenWeek = false) => {
    if (!dateValue) {
      return;
    }

    const monthId = dateValue.slice(0, 7);
    const nextMonth = timesheetMonths.find((month) => month.id === monthId);
    if (!nextMonth) {
      setToast("No timesheet calendar is configured for the selected date.");
      return;
    }

    const nextWeek = getWeekForTimesheetDate(nextMonth, dateValue);
    const nextDayId = getDayIdForDate(dateValue);
    const nextEntry = nextWeek?.entries?.[nextDayId];
    const nextEntryStatus = getTimesheetEntryStatus(nextEntry, nextMonth);
    const nextMonthChanged = nextMonth.id !== selectedTimesheetMonthId;
    if (nextMonthChanged) {
      setFilterLoading("time");
    }
    setSelectedTimesheetMonthId(nextMonth.id);
    setSelectedTimesheetDate(dateValue);
    setSelectedTimeWeekId(nextWeek?.id || nextMonth.weeks[0]?.id || "");
    setSelectedTimeDayId(nextDayId);
    if (nextMonthChanged) {
      setSelectedTimesheetCellIds([]);
    }

    if (shouldOpenWeek) {
      const nextMonthLockMessage = getTimesheetMonthLockMessage(nextMonth);
      if (nextMonthLockMessage || isLockedTimeStatus(nextEntryStatus)) {
        setToast(nextMonthLockMessage || "Submitted or approved timesheet entries cannot be edited.");
        return;
      }
      setActiveAction("time");
    }
  };

  const handleSubmitTimesheetApproval = async ({ confirmed = false, submitRequest = null } = {}) => {
    const activeCellIds = submitRequest?.cellIds || selectedCurrentMonthCellIds;
    if (!activeCellIds.length) {
      setToast("Select at least one timesheet box before submitting.");
      return false;
    }

    if (isTimesheetLocked) {
      setToast(timesheetLockMessage);
      return false;
    }

    const submitHoursError = submitRequest
      ? ""
      : getSelectedTimesheetSubmitError(
          activeCellIds,
          selectedTimesheetMonthId,
          monthTimesheet,
          selectedTimesheetMonth
        );
    if (submitHoursError) {
      setToast(submitHoursError);
      return false;
    }

    const entriesToSubmit = submitRequest?.entries || activeCellIds.map((cellId) => {
      const [, weekId, dayKey] = cellId.split(":");
      const week = monthTimesheet.find((monthWeek) => monthWeek.id === weekId);
      const entry = week?.entries?.[dayKey];
      const workDate = entry?.workDate || getWorkDateForWeekDay(selectedTimesheetMonthId, week, dayKey);
      return {
        weekId,
        dayKey,
        workDate,
        totalHours: getEntryHours(entry),
        status: getTimesheetMutationStatus(getTimesheetEntryStatus(entry, selectedTimesheetMonth)),
      };
    }).filter((entry) => entry.workDate);
    const cellIdsToSubmit = activeCellIds;
    const monthIdToSubmit = submitRequest?.monthId || selectedTimesheetMonthId;

    if (!entriesToSubmit.length) {
      setToast("Select a valid timesheet date before submitting.");
      return false;
    }

    if (!confirmed) {
      setTimesheetSubmitWarning({
        cellIds: [...cellIdsToSubmit],
        entries: [...entriesToSubmit].sort((left, right) => String(left.workDate).localeCompare(String(right.workDate))),
        monthId: monthIdToSubmit,
        monthLabel: selectedTimesheetMonth?.label || getMonthLabel(monthIdToSubmit),
      });
      return false;
    }

    setTimesheetSubmitWarning(null);
    setOperationLoading("time-submit");
    try {
      const submitResult = await submitTimeEntries(viewUserId, {
        month: monthIdToSubmit,
        entries: entriesToSubmit,
      });

      const submittedOn = new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      const selectedCellSet = new Set(cellIdsToSubmit);
      setTimesheetMonths((current) =>
        current.map((month) =>
          month.id === monthIdToSubmit
            ? {
                ...month,
                status: "Submitted",
                submittedOn,
                weeks: month.weeks.map((week) => ({
                  ...week,
                  entries: Object.fromEntries(
                    Object.entries(week.entries).map(([dayId, entry]) => {
                      const cellId = getTimesheetCellId(month.id, week.id, dayId);
                      const status = getTimesheetEntryStatus(entry, month);
                      if (!selectedCellSet.has(cellId) || isLockedTimeStatus(status)) {
                        return [dayId, entry];
                      }
                      return [dayId, { ...entry, status: "Submitted", submittedOn }];
                    })
                  ),
                })),
              }
            : month
        )
      );
      setSelectedTimesheetCellIds((current) =>
        current.filter((cellId) => !selectedCellSet.has(cellId))
      );
      const refreshed = await refreshSelectedTimesheetMonth({ manageLoading: false });
      const submitMessage = buildTimesheetSubmitMessage(submitResult, cellIdsToSubmit.length);
      setToast(
        refreshed
          ? submitMessage
          : "Timesheet entries were submitted, but the calendar could not be refreshed."
      );
      return true;
    } catch (error) {
      setToast(error.message || "Selected timesheet entries could not be submitted.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleCancelTimesheetSubmitWarning = () => {
    setTimesheetSubmitWarning(null);
  };

  const handleConfirmTimesheetSubmitWarning = () => {
    if (!timesheetSubmitWarning || operationLoading === "time-submit") {
      return;
    }
    handleSubmitTimesheetApproval({ confirmed: true, submitRequest: timesheetSubmitWarning });
  };

  const handleFillSelectedTime = async ({ description, entries = [], hours, project, projectId, task }) => {
    if (!selectedCurrentMonthCellIds.length) {
      setToast("Select at least one timesheet box before filling.");
      return false;
    }

    if (isTimesheetLocked) {
      setToast(timesheetLockMessage);
      return false;
    }

    const requestedEntries = (entries.length ? entries : [{ description, hours, project, projectId, task }]).map((entry) => ({
      description: trimStringValue(entry.description),
      hours: Number(entry.hours || 0),
      project: trimStringValue(entry.project),
      projectId: getCrmRecordId(entry.projectId),
      task: trimStringValue(entry.task),
      taskId: getCrmRecordId(entry.taskId),
    }));
    const totalRequestedHours = requestedEntries.reduce((sum, entry) => sum + entry.hours, 0);
    if (
      !requestedEntries.length ||
      requestedEntries.some((entry) => !entry.project || !entry.description || !Number.isFinite(entry.hours) || entry.hours <= 0) ||
      totalRequestedHours > REQUIRED_DAILY_TIMESHEET_HOURS
    ) {
      setToast("Every fill row needs project, description, positive hours, and the selected-date total cannot exceed 8 hours.");
      return false;
    }

    const normalizedEntries = [];
    for (const entry of requestedEntries) {
      const selectedProject = findProjectOption(timeProjectOptions, { projectId: entry.projectId, project: entry.project });
      if (!selectedProject) {
        setToast("Select a valid project from the list before filling time.");
        return false;
      }

      const selectedTask = entry.task ? findTaskOptionForProject(taskOptions, entry.task, selectedProject) : undefined;
      if (entry.task && !selectedTask) {
        setToast("Select a project entity that belongs to the selected project.");
        return false;
      }

      normalizedEntries.push({
        ...entry,
        project: selectedProject.name || entry.project,
        projectId: getProjectOptionId(selectedProject),
        task: selectedTask?.name || "",
        taskId: selectedTask?.id || "",
      });
    }

    const selectedCellSet = new Set(selectedCurrentMonthCellIds);
    const overLimitFillDate = selectedCurrentMonthCellIds
      .map((cellId) => {
        const [, weekId, dayId] = cellId.split(":");
        const week = monthTimesheet.find((monthWeek) => monthWeek.id === weekId);
        const entry = week?.entries?.[dayId];
        const status = getTimesheetEntryStatus(entry, selectedTimesheetMonth);
        const workDate = entry?.workDate || getWorkDateForWeekDay(selectedTimesheetMonthId, week, dayId);
        const lockedHours = getEntryItems(entry)
          .filter((item) => isReadOnlyTimeItem(item, item.status || status))
          .reduce((sum, item) => sum + Number(item.hours || 0), 0);
        return { workDate, lockedHours };
      })
      .find((date) => date.workDate && date.lockedHours + totalRequestedHours > REQUIRED_DAILY_TIMESHEET_HOURS);
    if (overLimitFillDate) {
      const availableHours = Math.max(REQUIRED_DAILY_TIMESHEET_HOURS - overLimitFillDate.lockedHours, 0);
      setToast(`${overLimitFillDate.workDate} already has ${Number(overLimitFillDate.lockedHours.toFixed(2))} locked leave hours. Fill only ${Number(availableHours.toFixed(2))}h for that date.`);
      return false;
    }

    const touchedWeeks = [];
    const nextWeeks = monthTimesheet.map((week) => {
      let touched = false;
      const entries = Object.fromEntries(
        Object.entries(week.entries).map(([dayId, entry]) => {
          const cellId = getTimesheetCellId(selectedTimesheetMonthId, week.id, dayId);
          if (!selectedCellSet.has(cellId)) {
            return [dayId, entry];
          }

          const status = getTimesheetEntryStatus(entry, selectedTimesheetMonth);
          const workDate = entry?.workDate || getWorkDateForWeekDay(selectedTimesheetMonthId, week, dayId);
          if (isLockedTimeStatus(status) || !workDate) {
            return [dayId, entry];
          }

          const lockedItems = getEntryItems(entry).filter((item) =>
            isReadOnlyTimeItem(item, item.status || status)
          );
          touched = true;
          return [
            dayId,
            {
              ...entry,
              status: "Draft",
              workDate,
              items: [
                ...lockedItems,
                ...normalizedEntries.map((normalizedEntry, index) => ({
                  id: `bulk-${week.id}-${dayId}-${index}-${Date.now()}`,
                  rowKey: `bulk-${week.id}-${dayId}-${index}-${Date.now()}`,
                  workDate,
                  hours: normalizedEntry.hours,
                  project: normalizedEntry.project,
                  projectId: normalizedEntry.projectId,
                  task: normalizedEntry.task,
                  taskId: normalizedEntry.taskId,
                  description: normalizedEntry.description,
                })),
              ],
            },
          ];
        })
      );
      const nextWeek = touched ? { ...week, status: "Draft", entries } : week;
      if (touched) {
        touchedWeeks.push(nextWeek);
      }
      return nextWeek;
    });

    if (!touchedWeeks.length) {
      setToast("Selected dates cannot be filled.");
      return false;
    }

    setOperationLoading("time-bulk-fill");
    try {
      const weekPayloads = touchedWeeks.map((week) => ({
        id: week.id,
        payload: buildWeekTimePayload(week.entries, selectedTimesheetMonthId, week),
      }));
      const payloadError = weekPayloads.map((week) => getWeekTimePayloadError(week.payload)).find(Boolean);
      if (payloadError) {
        setToast(payloadError);
        return false;
      }

      await Promise.all(
        weekPayloads.map((week) =>
          saveTimeWeek(
            viewUserId,
            selectedTimesheetMonthId,
            week.id,
            week.payload
          )
        )
      );

      const refreshed = await refreshSelectedTimesheetMonth({ manageLoading: false });
      if (!refreshed) {
        setTimesheetMonths((current) =>
          current.map((month) =>
            month.id === selectedTimesheetMonthId
              ? {
                  ...month,
                  status: "Draft",
                  weeks: month.weeks.map((week) => nextWeeks.find((nextWeek) => nextWeek.id === week.id) || week),
                }
              : month
          )
        );
      }
      setToast(
        refreshed
          ? `${selectedCurrentMonthCellIds.length} selected ${selectedCurrentMonthCellIds.length === 1 ? "date" : "dates"} filled.`
          : "Selected dates were filled, but the calendar could not be refreshed."
      );
      setActiveAction("");
      return true;
    } catch (error) {
      setToast(error.message || "Selected dates could not be filled.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleProjectEntityLogTime = async (entity = {}, { description, hours, project, projectId, task, taskId, workDate } = {}) => {
    const cleanWorkDate = trimStringValue(workDate);
    const cleanDescription = trimStringValue(description);
    const numericHours = Number(hours || 0);
    const cleanProjectId = getCrmRecordId(projectId || entity.projectId);
    const cleanTaskId = getCrmRecordId(taskId || entity.id);
    const projectName = trimStringValue(project || entity.projectName);
    const taskName = trimStringValue(task || entity.title || entity.key);

    if (!isIsoDateValue(cleanWorkDate)) {
      setToast("Select a valid date before logging time.");
      return false;
    }
    if (!projectName || !cleanDescription || !Number.isFinite(numericHours) || numericHours <= 0 || numericHours > REQUIRED_DAILY_TIMESHEET_HOURS) {
      setToast("Project, description, and 1-8 hours are required.");
      return false;
    }

    const targetMonthId = cleanWorkDate.slice(0, 7);
    const targetMonth = timesheetMonths.find((month) => month.id === targetMonthId) || createBlankTimesheetMonth(targetMonthId);
    const lockMessage = getTimesheetMonthLockMessage(targetMonth);
    if (lockMessage) {
      setToast(lockMessage);
      return false;
    }

    const targetWeek = getWeekForTimesheetDate(targetMonth, cleanWorkDate);
    const targetDayId = getDayIdForDate(cleanWorkDate);
    if (!targetWeek?.id || !targetDayId) {
      setToast("Timesheet week could not be resolved for the selected date.");
      return false;
    }
    const currentEntry = targetWeek?.entries?.[targetDayId] || { workDate: cleanWorkDate, status: "Draft", items: [] };
    const currentStatus = getTimesheetEntryStatus(currentEntry, targetMonth);
    if (isLockedTimeStatus(currentStatus)) {
      setToast("Submitted or approved timesheet entries cannot be edited.");
      return false;
    }
    const existingItems = getEntryItems(currentEntry);
    const nextDayTotal = existingItems.reduce((sum, item) => sum + Number(item.hours || 0), 0) + numericHours;
    if (nextDayTotal > REQUIRED_DAILY_TIMESHEET_HOURS) {
      setToast(`Daily timesheet total cannot exceed ${REQUIRED_DAILY_TIMESHEET_HOURS} hours.`);
      return false;
    }

    const nextEntry = {
      ...currentEntry,
      workDate: cleanWorkDate,
      status: "Draft",
      items: [
        ...existingItems,
        {
          id: `project-log-${entity.id || "entity"}-${Date.now()}`,
          workDate: cleanWorkDate,
          hours: numericHours,
          project: projectName,
          projectId: cleanProjectId,
          task: taskName,
          taskId: cleanTaskId,
          description: cleanDescription,
          status: "Draft",
        },
      ],
    };
    const nextEntries = {
      ...(targetWeek?.entries || {}),
      [targetDayId]: nextEntry,
    };
    const payload = buildWeekTimePayload(nextEntries, targetMonthId, targetWeek);
    const payloadError = getWeekTimePayloadError(payload);
    if (payloadError) {
      setToast(payloadError);
      return false;
    }

    setOperationLoading("project-time-log");
    try {
      await saveTimeWeek(viewUserId, targetMonthId, targetWeek.id, payload);
      let refreshed = false;
      try {
        const timeMonthPayload = await getTimeMonth(viewUserId, targetMonthId);
        setTimesheetMonths((current) => {
          const fallbackMonth = current.find((month) => month.id === targetMonthId) || targetMonth;
          const normalizedMonth = normalizeTimesheetMonth(timeMonthPayload, targetMonthId, fallbackMonth);
          return current.some((month) => month.id === normalizedMonth.id)
            ? current.map((month) => (month.id === normalizedMonth.id ? normalizedMonth : month))
            : [normalizedMonth, ...current];
        });
        refreshed = true;
      } catch {
        refreshed = false;
      }
      if (!refreshed) {
        setTimesheetMonths((current) => {
          const nextMonth = {
            ...targetMonth,
            status: "Draft",
            weeks: targetMonth.weeks.map((week) => (week.id === targetWeek.id ? { ...week, status: "Draft", entries: nextEntries } : week)),
          };
          return current.some((month) => month.id === targetMonthId)
            ? current.map((month) => (month.id === targetMonthId ? nextMonth : month))
            : [nextMonth, ...current];
        });
      }
      setToast(`${numericHours}h logged on ${entity.key || "project entity"}.`);
      return true;
    } catch (error) {
      setToast(error.message || "Time could not be logged for this entity.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleTimeCellEdit = (weekId, dayId = "") => {
    const entry = dayId ? monthTimesheet.find((week) => week.id === weekId)?.entries?.[dayId] : null;
    const entryStatus = dayId
      ? getTimesheetEntryStatus(entry, selectedTimesheetMonth)
      : "Draft";
    if (dayId && (entry?.isOutsideMonth || !entry?.workDate)) {
      setToast("Select a valid date in the month.");
      return;
    }
    if (!dayId && isTimesheetLocked) {
      setToast(timesheetLockMessage);
      return;
    }
    if (!dayId && isLockedTimeStatus(entryStatus)) {
      setToast(timesheetLockMessage || "Submitted or approved timesheet entries cannot be edited.");
      return;
    }

    setSelectedTimeWeekId(weekId || monthTimesheet[0]?.id);
    setSelectedTimeDayId(dayId);
    setActiveAction(dayId ? "timeEntry" : "time");
  };

  const handleTimesheetCellToggle = (weekId, dayId) => {
    const entry = monthTimesheet.find((week) => week.id === weekId)?.entries?.[dayId];
    const entryStatus = getTimesheetEntryStatus(entry, selectedTimesheetMonth);
    if (isTimesheetLocked) {
      setToast(timesheetLockMessage);
      return;
    }
    if (entry?.isOutsideMonth || !entry?.workDate) {
      setToast("Select a valid date in the month.");
      return;
    }
    if (isLockedTimeStatus(entryStatus)) {
      setToast("Submitted or approved timesheet entries cannot be selected.");
      return;
    }

    const cellId = getTimesheetCellId(selectedTimesheetMonthId, weekId, dayId);
    setSelectedTimesheetCellIds((current) =>
      current.includes(cellId) ? current.filter((selectedCellId) => selectedCellId !== cellId) : [...current, cellId]
    );
  };

  const handleSubmitExpense = async ({ expenseId, title, amount, description, expenseDate, billFile, category, resubmit }) => {
    const cleanTitle = trimStringValue(title);
    const cleanDescription = trimStringValue(description);
    const numericAmount = Number(amount || 0);
    const cleanExpenseDate = isIsoDateValue(expenseDate) ? expenseDate : getTodayDateKey();
    if (!cleanTitle || !cleanDescription || !Number.isFinite(numericAmount) || numericAmount <= 0) {
      setToast("Expense title, description, and an amount greater than zero are required.");
      return false;
    }

    setOperationLoading("expense-save");
    try {
      const billAttachment = billFile
        ? {
            fileName: billFile.name || "Expense bill",
            name: billFile.name || "Expense bill",
            contentType: billFile.type || "application/octet-stream",
            size: billFile.size,
            base64Data: await readFileAsBase64(billFile),
          }
        : null;
      const payload = {
        title: cleanTitle,
        description: cleanDescription,
        amount: numericAmount,
        currency: "INR",
        date: cleanExpenseDate,
        expenseDate: cleanExpenseDate,
        expenseType: trimStringValue(category) || "Other",
        category: trimStringValue(category) || "Other",
        type: trimStringValue(category) || "Other",
        resubmit: Boolean(resubmit),
        ...(billAttachment ? { attachments: [billAttachment] } : {}),
      };
      if (expenseId) {
        await updateExpense(viewUserId, expenseId, payload);
      } else {
        await createExpense(viewUserId, payload);
      }
      const refreshedExpenses = await getExpenses(viewUserId);
      if (viewScopeRef.current !== viewScope) return false;
      setExpenses(normalizeExpenses(refreshedExpenses));
      setToast(expenseId ? "Reimbursement updated." : billAttachment ? "Reimbursement submitted with bill." : "Reimbursement submitted.");
      setActiveAction("");
      setSelectedExpense(null);
      return true;
    } catch (error) {
      if (viewScopeRef.current === viewScope) setToast(error.message || "Reimbursement could not be saved.");
      return false;
    } finally {
      if (viewScopeRef.current === viewScope) setOperationLoading((current) => current === "expense-save" ? "" : current);
    }
  };

  const handleCancelExpense = async (expenseId) => {
    if (!expenseId) {
      return false;
    }
    if (!window.confirm("Cancel this reimbursement? This action cannot be undone.")) {
      return false;
    }

    setOperationLoading("expense-cancel");
    try {
      await cancelExpense(viewUserId, expenseId);
      const refreshedExpenses = await getExpenses(viewUserId);
      if (viewScopeRef.current !== viewScope) return false;
      setExpenses(normalizeExpenses(refreshedExpenses));
      setToast("Reimbursement cancelled.");
      setActiveAction("");
      setSelectedExpense(null);
      return true;
    } catch (error) {
      if (viewScopeRef.current === viewScope) setToast(error.message || "Reimbursement could not be cancelled.");
      return false;
    } finally {
      if (viewScopeRef.current === viewScope) setOperationLoading((current) => current === "expense-cancel" ? "" : current);
    }
  };

  const handleSubmitRequest = async (requestPayload) => {
    const subject = trimStringValue(typeof requestPayload === "string" ? requestPayload : requestPayload.subject);
    const description = trimStringValue(typeof requestPayload === "string" ? requestPayload : requestPayload.description);
    const requestType = trimStringValue(typeof requestPayload === "string" ? "General Query" : requestPayload.requestType) || "General Query";
    if (!subject) {
      setToast("Request subject is required.");
      return false;
    }

    setOperationLoading("request-save");
    try {
      const createdRequest = await createRequest(viewUserId, {
        subject,
        description: description || subject,
        requestType,
        type: requestType,
        priority: "Medium",
        ...(requestPayload.inlineFileIds?.length ? { inlineFileIds: requestPayload.inlineFileIds } : {}),
      });
      if (viewScopeRef.current !== viewScope) return false;
      setRequests((current) => [
        ...normalizeRequests([createdRequest]),
        ...current,
      ]);
      setToast("Request created.");
      setActiveAction("");
      return true;
    } catch (error) {
      if (viewScopeRef.current === viewScope) setToast(error.message || "Request could not be created.");
      return false;
    } finally {
      if (viewScopeRef.current === viewScope) setOperationLoading((current) => current === "request-save" ? "" : current);
    }
  };

  const toReferralFilePayload = async (file, label) => {
    if (!file) {
      return null;
    }
    return {
      fileName: file.name || `${label}.pdf`,
      name: file.name || `${label}.pdf`,
      contentType: file.type || "application/octet-stream",
      size: file.size,
      base64Data: await readFileAsBase64(file),
    };
  };

  const handleSubmitReferral = async (payload = {}) => {
    const role = trimStringValue(payload.role);
    const candidateName = trimStringValue(payload.candidateName || payload.name);
    const email = trimStringValue(payload.email);
    const phone = trimStringValue(payload.phone);
    const notes = trimStringValue(payload.notes);
    const selectedFiles = [payload.resumeFile, payload.coverLetterFile].filter(Boolean);

    if (!role || !candidateName || !email) {
      setToast("Role, candidate name, and email are required.");
      return false;
    }
    if (!payload.resumeFile) {
      setToast("Attach the candidate resume.");
      return false;
    }
    const invalidFileMessage = selectedFiles.map(getFileValidationError).find(Boolean);
    if (invalidFileMessage) {
      setToast(invalidFileMessage);
      return false;
    }

    setOperationLoading("referral-create");
    try {
      const resumeFile = await toReferralFilePayload(payload.resumeFile, "Resume");
      const coverLetterFile = await toReferralFilePayload(payload.coverLetterFile, "Cover Letter");
      const createdReferral = await createReferral(viewUserId, {
        role,
        candidateName,
        candidateEmail: email,
        phone,
        notes,
        resumeFile,
        coverLetterFile,
      });
      const data = unwrapApiData(createdReferral) || {};
      const referral = data.referral || data;
      const createdRequest = referral.request || data.request || null;
      const [requestRow] = createdRequest ? normalizeRequests([createdRequest]) : normalizeRequests(createdReferral);
      if (requestRow) {
        setRequests((current) => [requestRow, ...current.filter((item) => item.id !== requestRow.id)]);
      }
      setToast("Referral submitted to HR.");
      return true;
    } catch (error) {
      setToast(error.message || "Referral could not be submitted.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleOpenRequest = async (request) => {
    if (!request?.id) {
      return;
    }
    const requestGeneration = ++requestDetailRequestRef.current;
    const isCurrent = () => requestGeneration === requestDetailRequestRef.current && viewScopeRef.current === viewScope;
    setSelectedRequest(request);
    setRequestDetailLoading(true);
    try {
      const detailResponse = await getRequest(requestViewUserId, request.id);
      if (!isCurrent()) return;
      const [detail] = normalizeRequests(detailResponse);
      if (detail) {
        setSelectedRequest(detail);
        setRequests((current) => current.map((item) => (item.id === detail.id ? { ...item, ...detail } : item)));
        setWorkerRequests((current) => current.map((item) => (item.id === detail.id ? { ...item, ...detail } : item)));
      }
    } catch (error) {
      if (isCurrent()) setToast(error.message || "Request detail could not be loaded.");
    } finally {
      if (isCurrent()) setRequestDetailLoading(false);
    }
  };

  const handleCloseRequest = () => {
    requestDetailRequestRef.current += 1;
    setSelectedRequest(null);
    setRequestDetailLoading(false);
  };

  const handleUpdateRequest = async (requestId, payload = {}) => {
    if (!requestId) {
      setToast("Request could not be updated.");
      return false;
    }
    setOperationLoading("request-update");
    try {
      const response = await updateRequest(requestViewUserId, requestId, payload);
      const [updatedRequest] = normalizeRequests(response);
      if (updatedRequest) {
        setSelectedRequest((current) => (current?.id === updatedRequest.id ? { ...current, ...updatedRequest } : current));
        setRequests((current) => current.map((item) => (item.id === updatedRequest.id ? { ...item, ...updatedRequest } : item)));
        setWorkerRequests((current) => current.map((item) => (item.id === updatedRequest.id ? { ...item, ...updatedRequest } : item)));
      }
      setToast("Request updated.");
      return true;
    } catch (error) {
      setToast(error.message || "Request could not be updated.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleSubmitRequestComment = async (requestId, body, files = [], mentions = [], inlineFiles = []) => {
    const commentText = trimStringValue(body);
    const selectedFiles = Array.isArray(files) ? files : [];
    const selectedMentions = Array.isArray(mentions) ? mentions : [];
    if (!requestId || (!commentText && !selectedFiles.length)) {
      setToast("Add a comment or attach a file.");
      return false;
    }
    const invalidFileMessage = selectedFiles.map(getFileValidationError).find(Boolean);
    if (invalidFileMessage) {
      setToast(invalidFileMessage);
      return false;
    }
    setOperationLoading("request-comment");
    try {
      const uploadedFiles = [...inlineFiles, ...(selectedFiles.length ? await uploadRequestFiles(requestId, selectedFiles) : [])];
      const mentionValues = selectedMentions
        .map((mention) => getCrmRecordId(mention.contactId || mention.employeeSalesforceId || mention.id) || trimStringValue(mention.email))
        .filter(Boolean);
      const response = await addRequestComment(requestViewUserId, requestId, {
        body: commentText || "Added case attachment.",
        mentions: mentionValues,
        Mentioned__c: mentionValues.join(";"),
        fileIds: uploadedFiles.map((file) => file.fileId || file.id).filter(Boolean),
        attachments: uploadedFiles,
      });
      const [comment] = normalizeRequestComments(response);
      const [requestAttachmentPayload] = normalizeRequests({
        request: {
          id: requestId,
          attachments: (unwrapApiData(response) || {}).requestAttachments || uploadedFiles,
        },
      });
      const savedComment = {
        ...(comment || {
          id: `request-comment-${Date.now()}`,
          body: commentText || "Added case attachment.",
          authorName: user?.name || "You",
          createdAt: "Recently",
          attachments: uploadedFiles,
        }),
        mentions: comment?.mentions?.length ? comment.mentions : selectedMentions,
      };
      const savedCommentPreview = previewTextFromRichComment(savedComment.body);
      setSelectedRequest((current) => {
        if (!current || current.id !== requestId) {
          return current;
        }
        const knownFileIds = new Set((current.attachments || []).map((file) => file.fileId || file.id).filter(Boolean));
        const nextAttachments = [
          ...((requestAttachmentPayload?.attachments || uploadedFiles).filter((file) => {
            const fileKey = file.fileId || file.id;
            return !fileKey || !knownFileIds.has(fileKey);
          })),
          ...(current.attachments || []),
        ];
        return {
          ...current,
          comments: [...(current.comments || []), savedComment],
          commentCount: Number(current.commentCount || 0) + 1,
          attachmentCount: nextAttachments.length,
          attachments: nextAttachments,
          lastCommentPreview: savedCommentPreview,
          lastCommentBy: savedComment.authorName,
        };
      });
      setRequests((current) =>
        current.map((item) =>
          item.id === requestId
            ? {
                ...item,
                commentCount: Number(item.commentCount || 0) + 1,
                attachmentCount: Number(item.attachmentCount || 0) + uploadedFiles.length,
                lastCommentPreview: savedCommentPreview,
                lastCommentBy: savedComment.authorName,
                updated: "Recently",
              }
            : item
        )
      );
      setWorkerRequests((current) =>
        current.map((item) =>
          item.id === requestId
            ? {
                ...item,
                commentCount: Number(item.commentCount || 0) + 1,
                attachmentCount: Number(item.attachmentCount || 0) + uploadedFiles.length,
                lastCommentPreview: savedCommentPreview,
                lastCommentBy: savedComment.authorName,
                updated: "Recently",
              }
            : item
        )
      );
      setToast("Comment added.");
      return true;
    } catch (error) {
      setToast(error.message || "Comment could not be added.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const uploadRequestFiles = async (requestId, files = []) => {
    const cleanRequestId = getCrmRecordId(requestId);
    if (!cleanRequestId) {
      throw new Error("Request files cannot be uploaded because the Case ID is missing.");
    }

    const uploadedFiles = [];
    for (const file of files) {
      const base64Data = await readFileAsBase64(file);
      const slotResponse = await createFileUploadSlot(requestViewUserId, {
        parentType: "Case",
        parentId: cleanRequestId,
        recordId: cleanRequestId,
        requestId: cleanRequestId,
        caseId: cleanRequestId,
        linkedEntityId: cleanRequestId,
        fileName: file.name,
        name: file.name,
        contentType: file.type || "application/octet-stream",
        size: file.size,
        base64Data,
      });
      const slot = unwrapApiData(slotResponse) || {};
      const slotFileId = getCrmRecordId(slot.fileId || slot.contentDocumentId || slot.id);
      if (!slotFileId) {
        throw new Error(`${file.name} could not be uploaded.`);
      }
      uploadedFiles.push({
        id: slotFileId,
        fileId: slotFileId,
        versionId: getCrmRecordId(slot.versionId || slot.contentVersionId || slot.ContentVersionId),
        name: file.name,
        fileName: file.name,
        downloadUrl: getDownloadUrlFromPayload(slot),
        contentType: file.type || trimStringValue(slot.contentType),
        size: file.size,
        createdAt: "Just now",
      });
    }
    return uploadedFiles;
  };

  const handleProfileSave = async (section, payload) => {
    if (resourceErrors.profile || pendingResources.includes("profile")) {
      throw new Error("Refresh your profile before editing again.");
    }
    setOperationLoading(`profile-${section}`);
    try {
      const profileTargetContext = {
        employeeId: viewedUser?.employeeSalesforceId || (getCrmRecordId(viewUserId) ? viewUserId : ""),
        employeeSalesforceId: viewedUser?.employeeSalesforceId || (getCrmRecordId(viewUserId) ? viewUserId : ""),
        contactId: viewedUser?.employeeSalesforceId || (getCrmRecordId(viewUserId) ? viewUserId : ""),
        employeeEmail: viewedUser?.email || "",
      };
      const savePayload = {
        ...normalizeProfileSavePayload(section, payload),
        ...profileTargetContext,
      };
      const refreshSavedProfile = async () => {
        const targetViewUserId = viewUserId;
        try {
          const refreshedProfile = await getProfile(targetViewUserId);
          if (profileViewUserIdRef.current === targetViewUserId && viewScopeRef.current === viewScope) {
            setProfile(normalizeProfile(refreshedProfile, EMPTY_EMPLOYEE_PROFILE));
          }
        } catch {
          // A create may have succeeded without returning IDs. Require a read before another save.
          if (viewScopeRef.current === viewScope) {
            setResourceErrors((current) => ({ ...current, profile: "Your changes were saved, but the updated profile could not be loaded. Refresh before editing again." }));
          }
        }
      };
      if (section === "personal" || section === "contact") {
        await updateProfilePersonalContact(viewUserId, savePayload);
        await refreshSavedProfile();
        return true;
      }
      if (section === "emergency") {
        await updateEmergencyContacts(viewUserId, savePayload);
        await refreshSavedProfile();
        return true;
      }
      if (section === "insurance") {
        await updateInsuranceRelated(viewUserId, savePayload);
        await refreshSavedProfile();
        return true;
      }
      if (section === "addresses") {
        await updateAddresses(viewUserId, savePayload);
        await refreshSavedProfile();
        return true;
      }
      if (section === "payroll" || section === "bank") {
        await updateProfile(viewUserId, savePayload);
        await refreshSavedProfile();
        return true;
      }
      return false;
    } finally {
      if (viewScopeRef.current === viewScope) setOperationLoading((current) => current === `profile-${section}` ? "" : current);
    }
  };

  const refreshProfile = async () => {
    const targetViewUserId = viewUserId;
    const refreshedProfile = await getProfile(targetViewUserId);
    if (profileViewUserIdRef.current === targetViewUserId && viewScopeRef.current === viewScope) {
      setProfile(normalizeProfile(refreshedProfile, EMPTY_EMPLOYEE_PROFILE));
    }
  };

  const handleProfileDocumentUpload = async (documentName, file) => {
    const invalidFileMessage = getFileValidationError(file);
    if (invalidFileMessage) {
      setToast(invalidFileMessage);
      return false;
    }

    setOperationLoading("profile-document");
    try {
      const base64Data = await readFileAsBase64(file);
      await uploadProfileDocument(viewUserId, {
        documentName,
        documentType: documentName,
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        size: file.size,
        base64Data,
      });
      await refreshProfile();
      setToast(`${documentName} uploaded.`);
      return true;
    } catch (error) {
      setToast(error.message || `${documentName} could not be uploaded.`);
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleProfileDocumentDelete = async (document) => {
    const documentId = getCrmRecordId(document?.externalAttachmentId || document?.documentId || document?.id);
    if (!documentId) {
      setToast("Document cannot be deleted because its CRM ID is missing.");
      return false;
    }

    setOperationLoading("profile-document");
    try {
      await deleteProfileDocument(viewUserId, documentId);
      await refreshProfile();
      setToast(`${document?.documentName || document?.label || "Document"} deleted.`);
      return true;
    } catch (error) {
      setToast(error.message || "Document could not be deleted.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleProjectEntityStatusChange = async (entityId, status) => {
    const entity = projectEntities.find((item) => item.id === entityId);
    if (!entity || entity.status === status) {
      return false;
    }

    const cleanEntityId = getCrmRecordId(entityId);
    const cleanStatus = trimStringValue(status);
    if (!cleanEntityId || !cleanStatus) {
      setToast("Project entity cannot be saved because its CRM ID is missing.");
      return false;
    }

    const previousEntities = projectEntities;
    setOperationLoading(`project-save:${entityId}`);
    setProjectEntities((current) =>
      current.map((item) => (item.id === entityId ? { ...item, status: cleanStatus } : item))
    );

    try {
      await updateProjectEntity(viewUserId, cleanEntityId, buildProjectEntityStatusUpdatePayload(cleanStatus));
      setToast(`${entity.key || "Project entity"} moved to ${cleanStatus}.`);
      return true;
    } catch (error) {
      setProjectEntities(previousEntities);
      setToast(error.message || "Project entity status could not be updated.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleProjectEntityUpdate = async (entityId, payload) => {
    const entity = projectEntities.find((item) => item.id === entityId);
    if (!entity) {
      return false;
    }

    const cleanEntityId = getCrmRecordId(entityId);
    if (!cleanEntityId) {
      setToast("Project entity cannot be saved because its CRM ID is missing.");
      return false;
    }

    const savePayload = normalizeProjectEntityUpdatePayload(payload);
    const payloadError = getProjectEntityPayloadError(savePayload);
    if (payloadError) {
      setToast(payloadError);
      return false;
    }

    const previousEntities = projectEntities;
    setOperationLoading(`project-save:${entityId}`);
    setProjectEntities((current) =>
      current.map((item) => (item.id === entityId ? { ...item, ...payload, ...savePayload } : item))
    );

    try {
      await updateProjectEntity(viewUserId, cleanEntityId, savePayload);
      setToast(`${entity.key || "Project entity"} updated.`);
      return true;
    } catch (error) {
      setProjectEntities(previousEntities);
      setToast(error.message || "Project entity could not be updated.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleProjectEntityAttachFiles = async (entityId, files = []) => {
    const selectedFiles = Array.isArray(files) ? files : [];
    if (!selectedFiles.length) {
      return false;
    }
    const invalidFileMessage = selectedFiles.map(getFileValidationError).find(Boolean);
    if (invalidFileMessage) {
      setToast(invalidFileMessage);
      return false;
    }

    const cleanEntityId = getCrmRecordId(entityId);
    if (!cleanEntityId) {
      setToast("Project files cannot be uploaded because the entity CRM ID is missing.");
      return false;
    }

    setOperationLoading("project-file-upload");
    try {
      const uploadedFiles = await uploadProjectEntityFiles(cleanEntityId, selectedFiles);
      setProjectEntities((current) =>
        current.map((item) => {
          if (!projectEntityMatchesId(item, cleanEntityId) && !projectEntityMatchesId(item, entityId)) {
            return item;
          }
          const nextAttachments = mergeProjectFiles(uploadedFiles, item.attachments || []);
          return { ...item, attachments: nextAttachments, files: nextAttachments, attachmentCount: nextAttachments.length };
        })
      );
      try {
        await loadHrmData();
        setProjectEntities((current) =>
          mergeProjectEntityRows(current, [
            {
              id: cleanEntityId,
              attachments: uploadedFiles,
              files: uploadedFiles,
              attachmentCount: uploadedFiles.length,
            },
          ])
        );
      } catch {
        // Keep the optimistic attachment list if the refresh fails.
      }
      setToast(`${uploadedFiles.length || selectedFiles.length} file${(uploadedFiles.length || selectedFiles.length) === 1 ? "" : "s"} attached.`);
      return true;
    } catch (error) {
      setToast(error.message || "Project files could not be uploaded.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleProjectEntityComment = async (entityId, body, mentions = [], files = [], inlineFiles = []) => {
    const commentBody = body?.trim();
    const selectedFiles = Array.isArray(files) ? files : [];
    if (!commentBody && !selectedFiles.length) {
      return false;
    }
    const invalidFileMessage = selectedFiles.map(getFileValidationError).find(Boolean);
    if (invalidFileMessage) {
      setToast(invalidFileMessage);
      return false;
    }

    const cleanEntityId = getCrmRecordId(entityId);
    if (!cleanEntityId) {
      setToast("Project comment cannot be saved because the entity CRM ID is missing.");
      return false;
    }

    const optimisticComment = {
      id: `comment-${entityId}-${Date.now()}`,
      author: user?.name || "You",
      body: commentBody || "Added image attachment.",
      createdAt: "Just now",
      mentions,
      attachments: selectedFiles.map((file, index) => ({
        id: `pending-${file.name}-${index}`,
        name: file.name,
        fileName: file.name,
        size: file.size,
        contentType: file.type,
      })),
    };

    const previousEntities = projectEntities;
    const mentionValues = mentions
      .map((mention) => getCrmRecordId(mention.contactId || mention.employeeSalesforceId || mention.id) || trimStringValue(mention.email))
      .filter(Boolean);
    setOperationLoading("project-comment");
    setProjectEntities((current) =>
      current.map((item) =>
        item.id === entityId
          ? { ...item, comments: [optimisticComment, ...(item.comments || [])] }
          : item
      )
    );

    try {
      const uploadedFiles = [...inlineFiles, ...(selectedFiles.length ? await uploadProjectEntityFiles(cleanEntityId, selectedFiles) : [])];
      const savedCommentResponse = await addProjectEntityComment(viewUserId, cleanEntityId, {
        comment: optimisticComment.body,
        body: optimisticComment.body,
        Comment__c: optimisticComment.body,
        mentions: mentionValues,
        Mentioned__c: mentionValues.join(";"),
        fileIds: uploadedFiles.map((file) => file.fileId || file.id).filter(Boolean),
        attachments: uploadedFiles,
      });
      const savedCommentPayload = unwrapApiData(savedCommentResponse) || savedCommentResponse || {};
      const savedComment = savedCommentPayload.comment || savedCommentPayload;
      setProjectEntities((current) =>
        current.map((item) =>
          item.id === entityId
            ? {
                ...item,
                attachments: uploadedFiles.length ? [...uploadedFiles, ...(item.attachments || [])] : item.attachments,
                comments: (item.comments || []).map((comment) =>
                  comment.id === optimisticComment.id
                    ? {
                        ...optimisticComment,
                        ...savedComment,
                        body: savedComment.body || optimisticComment.body,
                        mentions: savedComment.mentions || mentions,
                        attachments: savedComment.attachments || uploadedFiles,
                      }
                    : comment
                ),
              }
            : item
        )
      );
      setToast("Comment added.");
      return true;
    } catch (error) {
      setProjectEntities(previousEntities);
      setToast(error.message || "Project comment could not be saved.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleProjectEntityChecklistAdd = async (entityId, label) => {
    const cleanEntityId = getCrmRecordId(entityId);
    const cleanLabel = trimStringValue(label);
    if (!cleanEntityId) {
      setToast("Checklist item cannot be added because the entity CRM ID is missing.");
      return false;
    }
    if (!cleanLabel) {
      setToast("Checklist item text is required.");
      return false;
    }

    setOperationLoading(`project-checklist:${entityId}`);
    try {
      await addProjectEntityChecklistItem(viewUserId, cleanEntityId, { label: cleanLabel });
      await loadProjectEntityDeepLink(cleanEntityId);
      return true;
    } catch (error) {
      setToast(error.message || "Checklist item could not be added.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleProjectEntityChecklistUpdate = async (entityId, checklistItemId, payload) => {
    const cleanEntityId = getCrmRecordId(entityId);
    const cleanChecklistItemId = getCrmRecordId(checklistItemId);
    if (!cleanEntityId || !cleanChecklistItemId) {
      setToast("Checklist item cannot be updated because its CRM ID is missing.");
      return false;
    }

    setOperationLoading(`project-checklist:${entityId}`);
    try {
      await updateProjectEntityChecklistItem(viewUserId, cleanEntityId, cleanChecklistItemId, payload);
      await loadProjectEntityDeepLink(cleanEntityId);
      return true;
    } catch (error) {
      setToast(error.message || "Checklist item could not be updated.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleCreateProject = async (payload) => {
    if (!actorCanCreateProject) {
      setToast("Only Mayank and Shiv can create projects.");
      return false;
    }

    const savePayload = normalizeProjectCreatePayload(payload);
    if (!savePayload.title) {
      setToast("Project title is required.");
      return false;
    }
    if (!savePayload.projectManagerId) {
      setToast("Select a valid project manager before creating the project.");
      return false;
    }
    if (!savePayload.accountId) {
      setToast("Select an account before creating the project.");
      return false;
    }
    const projectColor = trimStringValue(payload.color || payload.colour);
    if (projectColor && !/^#[0-9a-fA-F]{6}$/.test(projectColor)) {
      setToast("Project color must be a hex code like #2563EB.");
      return false;
    }

    setOperationLoading("project-create");
    try {
      const createdProject = await createProject(viewUserId, savePayload);
      const normalizedProjects = normalizeProjects(createdProject);
      if (normalizedProjects.length) {
        setProjectOptions((current) => [...normalizedProjects, ...current]);
      }
      await loadHrmData();
      setToast("Project created.");
      return true;
    } catch (error) {
      setToast(error.message || "Project could not be created.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleProjectAccountSearch = useCallback(async (search = "") => {
    if (!actorCanCreateProject) {
      throw new Error("Only Mayank and Shiv can create projects.");
    }
    try {
      const response = await searchProjectAccounts(viewUserId, search);
      return normalizeProjectAccounts(response);
    } catch (error) {
      setToast(error.message || "Accounts could not be loaded.");
      throw error;
    }
  }, [actorCanCreateProject, viewUserId]);

  const uploadProjectEntityFiles = async (entityId, files = []) => {
    const uploadedFiles = [];

    for (const file of files) {
      const base64Data = await readFileAsBase64(file);
      const slotResponse = await createFileUploadSlot(viewUserId, {
        parentType: "ProjectEntity",
        parentId: entityId,
        recordId: entityId,
        projectEntityId: entityId,
        entityId,
        linkedEntityId: entityId,
        fileName: file.name,
        name: file.name,
        contentType: file.type || "application/octet-stream",
        size: file.size,
        base64Data,
      });
      const slot = unwrapApiData(slotResponse) || {};
      const slotFileId = getCrmRecordId(slot.fileId || slot.contentDocumentId || slot.id);
      if (slotFileId && (slot.uploaded || slot.linked || !slot.uploadUrl)) {
        uploadedFiles.push({
          id: slotFileId,
          fileId: slotFileId,
          versionId: getCrmRecordId(slot.versionId || slot.contentVersionId || slot.ContentVersionId),
          name: file.name,
          fileName: file.name,
          downloadUrl: getDownloadUrlFromPayload(slot),
          contentType: file.type || trimStringValue(slot.contentType),
          size: file.size,
          createdAt: "Just now",
        });
        continue;
      }
      const uploadUrl = getUploadUrlFromPayload(slot);
      if (!uploadUrl) {
        throw new Error("File upload slot did not return an upload URL.");
      }

      const uploadMethod = trimStringValue(slot.uploadMethod || slot.method) || "PUT";
      if (slot.fields && typeof slot.fields === "object") {
        const formData = new FormData();
        Object.entries(slot.fields).forEach(([key, value]) => formData.append(key, value));
        formData.append("file", file);
        const uploadResponse = await fetch(uploadUrl, {
          method: uploadMethod,
          body: formData,
        });
        if (!uploadResponse.ok) {
          throw new Error(`${file.name} could not be uploaded.`);
        }
      } else {
        const uploadResponse = await fetch(uploadUrl, {
          method: uploadMethod,
          headers: getFileHeaders(slot.headers, file),
          body: file,
        });
        if (!uploadResponse.ok) {
          throw new Error(`${file.name} could not be uploaded.`);
        }
      }

      uploadedFiles.push({
        id: getCrmRecordId(slot.fileId || slot.contentDocumentId || slot.id) || `${entityId}-${file.name}`,
        fileId: getCrmRecordId(slot.fileId || slot.contentDocumentId || slot.id),
        versionId: getCrmRecordId(slot.versionId || slot.contentVersionId || slot.ContentVersionId),
        name: file.name,
        fileName: file.name,
        downloadUrl: getDownloadUrlFromPayload(slot),
        contentType: file.type || trimStringValue(slot.contentType),
        size: file.size,
        createdAt: "Just now",
      });
    }

    return uploadedFiles;
  };

  const handleCreateProjectEntity = async (payload) => {
    const selectedFiles = Array.isArray(payload?.files) ? payload.files : [];
    const invalidFileMessage = selectedFiles.map(getFileValidationError).find(Boolean);
    if (invalidFileMessage) {
      setToast(invalidFileMessage);
      return false;
    }

    const savePayload = normalizeProjectEntityPayload(payload);
    const payloadError = getProjectEntityPayloadError(savePayload, {
      requireProject: true,
      requireAssignee: true,
    });
    if (payloadError) {
      setToast(payloadError);
      return false;
    }

    setOperationLoading("project-entity-create");
    try {
      const createdEntity = await createProjectEntity(viewUserId, savePayload);
      const normalizedEntities = normalizeProjectEntities(createdEntity);
      const createdEntityId = getCreatedProjectEntityId(createdEntity, normalizedEntities);
      let uploadedFiles = [];
      let uploadWarning = "";

      if (selectedFiles.length && !createdEntityId) {
        setToast("Project entity created, but files could not be uploaded because CRM did not return an entity ID.");
        await loadHrmData();
        return true;
      }
      if (selectedFiles.length) {
        try {
          uploadedFiles = await uploadProjectEntityFiles(createdEntityId, selectedFiles);
        } catch (uploadError) {
          uploadWarning = `Project entity created, but ${uploadError.message || "selected files could not be uploaded."}`;
        }
      }

      if (normalizedEntities.length) {
        setProjectEntities((current) => [
          ...normalizedEntities.map((entity) => ({
            ...entity,
            attachments: uploadedFiles.length ? mergeProjectFiles(uploadedFiles, entity.attachments || []) : entity.attachments,
            files: uploadedFiles.length ? mergeProjectFiles(uploadedFiles, entity.files || entity.attachments || []) : entity.files,
          })),
          ...current,
        ]);
      }
      await loadHrmData();
      if (createdEntityId && (normalizedEntities.length || uploadedFiles.length)) {
        const refreshedRows = normalizedEntities.length
          ? normalizedEntities.map((entity) => ({
              ...entity,
              attachments: uploadedFiles.length ? mergeProjectFiles(uploadedFiles, entity.attachments || []) : entity.attachments,
              files: uploadedFiles.length ? mergeProjectFiles(uploadedFiles, entity.files || entity.attachments || []) : entity.files,
              attachmentCount: uploadedFiles.length ? Math.max(Number(entity.attachmentCount || 0), uploadedFiles.length) : entity.attachmentCount,
            }))
          : [
              {
                id: createdEntityId,
                attachments: uploadedFiles,
                files: uploadedFiles,
                attachmentCount: uploadedFiles.length,
              },
            ];
        setProjectEntities((current) => mergeProjectEntityRows(current, refreshedRows));
      }
      setToast(uploadWarning || (selectedFiles.length ? "Project entity created with files." : "Project entity created."));
      return true;
    } catch (error) {
      setToast(error.message || "Project entity could not be created.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleProjectEntityFileDownload = async (file) => {
    if (file?.downloadUrl) {
      window.open(file.downloadUrl, "_blank", "noopener,noreferrer");
      return true;
    }

    const fileId = getCrmRecordId(file?.fileId || file?.id || file?.contentDocumentId || file?.versionId);
    if (!fileId) {
      setToast("File download link is not available from CRM.");
      return false;
    }

    const downloadWindow = window.open("", "_blank");
    if (downloadWindow) {
      downloadWindow.opener = null;
    }
    setOperationLoading("project-file-download");
    try {
      const downloadResponse = await downloadFile(requestViewUserId, fileId);
      const downloadUrl = getDownloadUrlFromPayload(downloadResponse);
      if (!downloadUrl) {
        downloadWindow?.close();
        setToast("File download link is not available from CRM.");
        return false;
      }
      if (downloadWindow) {
        downloadWindow.location.href = downloadUrl;
      } else {
        window.location.assign(downloadUrl);
      }
      return true;
    } catch (error) {
      downloadWindow?.close();
      setToast(error.message || "File could not be downloaded.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleRequestFileDownload = async (file) => {
    if (file?.downloadUrl) {
      window.open(file.downloadUrl, "_blank", "noopener,noreferrer");
      return true;
    }

    const fileId = getCrmRecordId(file?.fileId || file?.id || file?.contentDocumentId || file?.versionId);
    if (!fileId) {
      setToast("File download link is not available from CRM.");
      return false;
    }

    const downloadWindow = window.open("", "_blank");
    if (downloadWindow) {
      downloadWindow.opener = null;
    }
    setOperationLoading("request-file-download");
    try {
      const downloadResponse = await downloadFile(viewUserId, fileId);
      const downloadUrl = getDownloadUrlFromPayload(downloadResponse);
      if (!downloadUrl) {
        downloadWindow?.close();
        setToast("File download link is not available from CRM.");
        return false;
      }
      if (downloadWindow) {
        downloadWindow.location.href = downloadUrl;
      } else {
        window.location.assign(downloadUrl);
      }
      return true;
    } catch (error) {
      downloadWindow?.close();
      setToast(error.message || "File could not be downloaded.");
      return false;
    } finally {
      setOperationLoading("");
    }
  };

  const handleToggleTask = (taskId) => {
    setOnboardingTasks((current) =>
      current.map((task) => (task.id === taskId ? { ...task, done: !task.done } : task))
    );
  };

  const handlePolicyOpen = (policy) => {
    setSelectedPolicy(policy);
    setActiveAction("");
    setActiveNav("policies");
  };

  const handlePayslipSelect = (payslip = null) => {
    if (!payslip?.id) {
      setToast("No posted salary slip is available yet.");
      return;
    }
    setSelectedPayslip(payslip);
    setActiveAction("payslip");
  };

  const handleAttendanceToggle = async () => {
    if (pendingResources.includes("attendance") || resourceErrors.attendance) {
      setToast(resourceErrors.attendance || "Attendance is still loading. Please try again shortly.");
      return;
    }
    const actionAt = new Date();
    const attendanceDate = getAttendanceDateKey(actionAt);
    const existingAttendanceDate = getAttendanceRecordDateKey(attendance, actionAt);
    const isPresentToday =
      existingAttendanceDate === attendanceDate &&
      Boolean(attendance.status === "checked-in" || attendance.checkInAt || attendance.checkInTime);
    if (isPresentToday) {
      setToast("Attendance is already marked present.");
      return;
    }
    if (!attendanceNetworkAccess.resolved) {
      setToast("Checking the office network before marking attendance.");
      return;
    }
    if (!attendanceNetworkAccess.allowed) {
      setToast(attendanceNetworkAccess.message || "Attendance can only be marked from the Antrazal office network.");
      return;
    }

    setOperationLoading("attendance-save");
    try {
      await checkIn(viewUserId, {
        attendanceDate,
        workDate: attendanceDate,
        date: attendanceDate,
        overwriteCheckIn: true,
        clearCheckOut: true,
      });
      if (viewScopeRef.current !== viewScope) return;
      await loadHrmData();
      if (viewScopeRef.current !== viewScope) return;
      const attendanceDay = new Date(`${attendanceDate}T00:00:00`);
      setAttendance((current) => ({
        ...current,
        status: "checked-in",
        date: getAttendanceLogDate(attendanceDay),
        loggedDate: getAttendanceLogDate(attendanceDay),
        rawLoggedDate: attendanceDate,
        checkInAt: actionAt.toISOString(),
        checkInTime: getAttendanceClockTime(actionAt),
        checkOutAt: "",
        checkOutTime: "",
      }));
      setToast("Attendance marked present.");
    } catch (error) {
      if (viewScopeRef.current === viewScope) setToast(error.message || "Attendance action failed.");
    } finally {
      if (viewScopeRef.current === viewScope) setOperationLoading((current) => current === "attendance-save" ? "" : current);
    }
  };

  if (!authResolved) {
    return (
      <div className="new-hrm-premium-root new-hrm-app--premium">
        <div className="new-hrm-session-loading" role="status" aria-live="polite">
          <span className="new-hrm-session-loading__spinner" aria-hidden="true" />
          <span>Opening HRM</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="new-hrm-premium-root new-hrm-app--premium">
        <HrmLogin onLogin={handleLogin} />
        <Toast message={toast} onClose={() => setToast("")} />
      </div>
    );
  }

  return (
    <div className="new-hrm-premium-root new-hrm-app--premium">
      <DashboardLayout
        activeNav={activeNav}
        adminUsersLoading={adminUsersLoading}
        assetAllocations={assetAllocations}
        attendance={attendance}
        attendanceMarkingAllowed={attendanceNetworkAccess.allowed && !resourceErrors.attendance && !pendingResources.includes("attendance")}
        attendanceNetworkMessage={resourceErrors.attendance || attendanceNetworkAccess.message}
        attendanceNetworkResolved={attendanceNetworkAccess.resolved}
        dataError={screenDataError}
        profileSaveBlocked={Boolean(resourceErrors.profile)}
        dataReady={dataScope === viewScope && ["attendance", "leaveRequests", "holidays", "entities"].every((key) => !pendingResources.includes(key) && !resourceErrors[key])}
        dataLoading={screenDataLoading}
        filterLoading={filterLoading}
        directoryPeople={directoryPeople}
        expenses={expenses}
        filteredPolicies={filteredPolicies}
        forms={formRows}
        formsActionRequired={formRows.some((form) => form.requiresAction)}
        holidays={holidays}
        isAdmin={isAdmin}
        canCreateProject={viewedCanCreateProject}
        canViewEvents={canViewEvents}
        canViewMyCases={canViewMyCases}
        defaulterLeaderboard={defaulterLeaderboard}
        viewedUser={viewedUser}
        leaveBalances={effectiveLeaveBalances}
        leaveCredits={leaveCredits}
        leaveRequests={leaveRequests}
        managedUsers={managedUsers}
        monthTimesheet={monthTimesheet}
        onAction={handleActionOpen}
        onAssetsLoaded={handleAssetsLoaded}
        onAttendanceToggle={handleAttendanceToggle}
        onDownloadPayslip={handleDownloadPayslip}
        onCloseEvent={handleCloseEvent}
        onLogout={handleLogout}
        onManagedUserChange={handleManagedUserChange}
        onNavigate={handleNavigate}
        onOpenEvent={handleOpenEvent}
        onProjectAccountSearch={handleProjectAccountSearch}
        onCreateProject={handleCreateProject}
        onCreateProjectEntity={handleCreateProjectEntity}
        onProjectEntityAttachFiles={handleProjectEntityAttachFiles}
        onProjectEntityChecklistAdd={handleProjectEntityChecklistAdd}
        onProjectEntityChecklistUpdate={handleProjectEntityChecklistUpdate}
        onProjectEntityComment={handleProjectEntityComment}
        onProjectEntityFileDownload={handleProjectEntityFileDownload}
        onProjectEntityFiltersChange={handleProjectEntityFiltersChange}
        onProjectEntityLogTime={handleProjectEntityLogTime}
        onProjectEntityStatusChange={handleProjectEntityStatusChange}
        onProjectEntityUpdate={handleProjectEntityUpdate}
        onFormsChange={setFormRows}
        onPolicyOpen={handlePolicyOpen}
        onProfileDocumentDelete={handleProfileDocumentDelete}
        onProfileDocumentUpload={handleProfileDocumentUpload}
        onProfileSave={handleProfileSave}
        onPayslipSelect={handlePayslipSelect}
        onPoliciesChange={setPolicyRows}
        onCloseRequest={handleCloseRequest}
        onOpenRequest={handleOpenRequest}
        onReloadData={handleReloadHrmData}
        onRequestFileDownload={handleRequestFileDownload}
        onSubmitRequestComment={handleSubmitRequestComment}
        onRequestInlineImageUpload={(id, file, options) => uploadHrmInlineImage(requestViewUserId, 'Case', id, file, options)}
        onSubmitReferral={handleSubmitReferral}
        onUpdateRequest={handleUpdateRequest}
        onRefreshTimesheet={() => refreshSelectedTimesheetMonth({ showToast: true })}
        onSubmitTime={handleSubmitTime}
        onSubmitTimesheetApproval={handleSubmitTimesheetApproval}
        onTimeCellEdit={handleTimeCellEdit}
        onTimesheetCellToggle={handleTimesheetCellToggle}
        onTimesheetMonthChange={handleTimesheetMonthChange}
        payrollSlips={payrollSlips}
        profile={profile}
        projectEntityDeepLinkId={projectEntityDeepLinkId}
        projectEntities={projectEntities}
        projectOptions={projectOptions}
        timeProjectOptions={timeProjectOptions}
        taskOptions={taskOptions}
        searchTerm={searchTerm}
        selectedManagedUserId={selectedManagedUserId}
        selectedTimesheetCellIds={selectedTimesheetCellIds}
        selectedTimesheetCellCount={selectedCurrentMonthCellIds.length}
        selectedTimesheetDate={selectedTimesheetDate}
        selectedTimesheetMonth={selectedTimesheetMonth}
        selectedTimesheetMonthId={selectedTimesheetMonthId}
        timesheetMonths={timesheetMonths}
        isTimesheetLocked={isTimesheetLocked}
        timesheetLockMessage={timesheetLockMessage}
        onTimesheetDateSelect={handleTimesheetDateSelect}
        operationLoading={operationLoading}
        totalHours={totalHours}
        user={user}
        viewUserId={viewUserId}
        requests={requests}
        workerRequests={workerRequests}
        requestDetailLoading={requestDetailLoading}
        selectedRequest={selectedRequest}
        selectedEventId={selectedEventId}
      />
      <ActionModal
        action={activeAction}
        currentPayslip={selectedPayslip}
        dataLoading={dataLoading || timeDataLoading}
        directoryPeople={directoryPeople}
        expenses={expenses}
        holidays={holidays}
        leaveBalances={effectiveLeaveBalances}
        leaveTypes={leaveTypes}
        leaveRequests={leaveRequests}
        selectedLeaveRequest={selectedLeaveRequest}
        selectedExpense={selectedExpense}
        monthTimesheet={monthTimesheet}
        onboardingTasks={onboardingTasks}
        onClose={handleActionClose}
        onCancelExpense={handleCancelExpense}
        onCancelLeave={handleCancelLeave}
        onDownloadPayslip={handleDownloadPayslip}
        onFillSelectedTime={handleFillSelectedTime}
        onSubmitExpense={handleSubmitExpense}
        onSubmitRequest={handleSubmitRequest}
        onRequestInlineImageUpload={(file, options) => uploadHrmInlineImage(viewUserId, 'InlineDraft', null, file, options)}
        onSubmitLeave={handleSubmitLeave}
        onSubmitTime={handleSubmitTime}
        onSubmitTimesheetApproval={handleSubmitTimesheetApproval}
        onTimeCellEdit={handleTimeCellEdit}
        onTimesheetCellToggle={handleTimesheetCellToggle}
        onTimesheetMonthChange={handleTimesheetMonthChange}
        onToggleTask={handleToggleTask}
        selectedPolicy={selectedPolicy}
        filterLoading={filterLoading}
        operationLoading={operationLoading}
        projectOptions={timeProjectOptions}
        selectedTimesheetCellIds={selectedTimesheetCellIds}
        selectedTimesheetCellCount={selectedCurrentMonthCellIds.length}
        selectedTimesheetDate={selectedTimesheetDate}
        selectedTimesheetMonth={selectedTimesheetMonth}
        selectedTimesheetMonthId={selectedTimesheetMonthId}
        selectedTimeDayId={selectedTimeDayId}
        selectedTimeWeek={selectedTimeWeek}
        timesheetMonths={timesheetMonths}
        taskOptions={taskOptions}
        isTimesheetLocked={isTimesheetLocked}
        timesheetLockMessage={timesheetLockMessage}
        onTimesheetDateSelect={handleTimesheetDateSelect}
        requests={requests}
      />
      {timesheetSubmitWarning && (
        <div className="new-hrm-modal-backdrop new-hrm-submit-warning-backdrop" role="presentation">
          <section
            className="new-hrm-modal new-hrm-submit-warning-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="timesheet-submit-warning-title"
          >
            <header className="new-hrm-modal__header">
              <div>
                <p className="new-hrm-modal-note">Timesheet submission warning</p>
                <h2 id="timesheet-submit-warning-title">Submit selected dates?</h2>
              </div>
              <button
                className="new-hrm-icon-button"
                type="button"
                onClick={handleCancelTimesheetSubmitWarning}
                disabled={operationLoading === "time-submit"}
                aria-label="Close submit warning"
              >
                x
              </button>
            </header>
            <div className="new-hrm-submit-warning-modal__body">
              <p>
                You will not be able to move these timecards back to draft from HRM after submission.
                Your timecards may be approved immediately after submission and become locked.
              </p>
              <strong>{timesheetSubmitWarning.monthLabel}</strong>
              <ul>
                {timesheetSubmitWarning.entries.map((entry) => (
                  <li key={`${entry.weekId}-${entry.dayKey}-${entry.workDate}`}>
                    <span>{formatTimesheetSubmitDate(entry.workDate)}</span>
                    <small>{Number(entry.totalHours || 0)}h · {entry.status || "Draft"}</small>
                  </li>
                ))}
              </ul>
            </div>
            <div className="new-hrm-submit-warning-modal__actions">
              <button
                className="new-hrm-secondary-button"
                type="button"
                onClick={handleCancelTimesheetSubmitWarning}
                disabled={operationLoading === "time-submit"}
              >
                Cancel
              </button>
              <button
                className="new-hrm-primary-button new-hrm-submit-warning-modal__confirm"
                type="button"
                onClick={handleConfirmTimesheetSubmitWarning}
                disabled={operationLoading === "time-submit"}
              >
                {operationLoading === "time-submit" ? "Submitting..." : "I understand, submit"}
              </button>
            </div>
          </section>
        </div>
      )}
      <Toast message={toast} onClose={() => setToast("")} />
    </div>
  );
};

export default NewHrmApp;
